import { inCity } from "@shared/net/city";
import { districtName, enteredLine, fullWakeLine, roundOverLine, wakeBeginsLine } from "./hud/district";
import { runView, type RunView } from "@shared/sim/run";
import type { RunMsg } from "@shared/net/protocol";
import { loadSettings, type Settings } from "./settings";
import { skinByToken } from "@shared/economy/catalog";
import { MAX_CATCHUP_TICKS, MOVE, SIM_DT, SIM_HZ } from "@shared/sim/constants";
import { TRAM_CABIN, tramAboard, tramHail } from "@shared/sim/tram";
import { WAKE } from "@shared/sim/wake";
import type { InputFrame } from "@shared/sim/input";
import { FILE_APARTMENT_ID } from "@shared/sim/apartment";
import { SCRIP_PIT_ID } from "@shared/sim/pit";
import { CITY_LIMIT_ID } from "@shared/sim/limit";
import { GREEN_HOLD_ID } from "@shared/sim/preserve";
import { DEFAULT_LEVEL_ID, levelById, LEVEL_IDS, levelDisplayName } from "@shared/sim/level";
import { itemName } from "@shared/manifest/items";
import { eyeHeight, eyePos, reviveMotion, type PlayerState } from "@shared/sim/player";
import { arrivalFromQuery } from "@shared/net/citygates";
import { contestOf, contestRespawn, fixerOf, inContest } from "@shared/city/contest";
import { inStreetTalk, streetLine } from "@shared/city/talk";
import { streetCast } from "@shared/city/continents";
import type { SpawnPoint } from "@shared/sim/level";
import { canSee, MECH, WASP } from "@shared/sim/ai";
import { aimAssistScale } from "./aimassist";
import { DUMMY_HEIGHT, DUMMY_RADIUS, hashWorld, MECH_HEIGHT, MECH_RADIUS, WASP_HEIGHT, WASP_RADIUS, World, type SimEvent } from "@shared/sim/world";
import { lenXZ, wrapAngle } from "@shared/math/vec3";
import { GameAudio, sirenWait } from "./audio";
import { Bot, type BotStep, type BotTarget } from "./bot";
import { Hud } from "./hud/hud";
import { GhostFile } from "./file";
import { InputController } from "./input";
import { TouchControls, wantsTouch } from "./touch";
import { PerfMonitor } from "./perf";
import { Renderer, type ViewState } from "./render/renderer";
import { perfStep, perfWindow, type PerfWindow } from "./hud/perf";
import type { AimTarget } from "./render/tps";
import type { ArcSpec } from "./render/ballistic";
import { hitMarks, pruneHits, wedgeLife, type HitSource } from "./hud/damage";
import { impactRead, landedDamage, WASP_SHOT } from "./hit";
import { bodyKey, closeLine, closeRead, forgetOldHits, rememberHit, type LandedHit, ttkNote, victimLabel, weaponName } from "./hud/kill";
import { challengeClearedLine, gateFor, MAX_RANK, masteryRankLine } from "@shared/progression/mastery";
import { threatMarks, type LiveProjectile } from "./hud/threat";
import { waspLocks, type WaspSeen } from "./vantage";
import { lookYawPitch } from "./render/feel";
import { stepCues, strideOf, type Walker } from "./steps";
import { gunCue } from "./gunfire";
import { shotPass } from "./nearmiss";
import { altPadHot, altPadLow, ammoRead, firePadHot, firePadLow, grenadePadHot, grenadePadLow, jumpPadHot, jumpPadLow, lastRoundsEdge, nextGrenadePadHot, nextGrenadePadLow, pausePadHot, pausePadLow, reloadPadHot, reloadPadLow, slidePadHot, slidePadLow, stickKnobHot, stickKnobLow, stickRingHot, stickRingLow, weaponPadHot, weaponPadLow } from "./hud/ammo";
import { momentLine, runMoments } from "./runcue";
import { shieldLine, shieldMoments } from "./shieldcue";
import { freshestVantage, targetRead } from "./hud/target";
import { prunePings, rememberPing, type Ping } from "./hud/ping";
import { coneNow, coneRadiusPx } from "./hud/spread";
import { kernelIn, nearestNode, nodeReadout, trackHolds, type TrackedNode } from "./hud/node";
import { NetClient } from "./net/netclient";
import { SimulatedLink, WsTransport, type LinkSim } from "./net/transport";
import { ENT_CLOUD, ENT_MECH, ENT_NODE, ENT_PROJECTILE, ENT_WASP, FX, meleeOfWire, WEAPON_WIRE, type NetInput, type Snapshot as NetSnapshot, type SocialMsg } from "@shared/net/protocol";
import { glyphFor, glyphSvg } from "@shared/identity/glyph";
import { RangeGhost } from "./ghost";
import { Campaign } from "./campaign";
import { AUDITS } from "@shared/endgame/audits";
import { emptyInput } from "@shared/sim/input";
import { trophiesFromLedger } from "./render/hub";
import { officeVisitor } from "@shared/campaign/save";
import { monikerById } from "@shared/identity/monikers";
import { filesWord } from "@shared/identity/identity";
import type { NodeView } from "./render/wake";
import { GRENADE_LIST, WEAPONS, WEAPON_LIST } from "@shared/weapons/manifest";
import { modsFor, weaponDefOf } from "@shared/sim/player";
import { rejoinDelay, rejoinTries, triesWord } from "@shared/net/rejoin";
import { linkStatusLine, linkingSimNote, roomName } from "./hud/room";
import { loadingFor, placeName, travelTo } from "./loading";
import { WorldMap } from "./worldmap";

export interface NetConfig {
  url: string;
  name: string;
  token?: string;
  sim?: LinkSim;
}

interface Snapshot {
  x: number;
  y: number;
  z: number;
  eye: number;
  yaw: number;
  pitch: number;
  kickPitch: number;
  kickYaw: number;
}

const snap = (p: PlayerState): Snapshot => ({
  x: p.pos.x,
  y: p.pos.y,
  z: p.pos.z,
  eye: eyeHeight(p),
  yaw: p.yaw,
  pitch: p.pitch,
  kickPitch: p.weapon.kickPitch,
  kickYaw: p.weapon.kickYaw,
});

const lerpAngle = (a: number, b: number, t: number): number => {
  let d = b - a;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
};

/**
 * Client game: fixed-timestep simulation (SIM_HZ) decoupled from rendering
 * (rAF), with render-time interpolation between the last two sim states.
 */
/** what a projectile entity's `a` byte means on the wire */
const PROJ_KINDS = ["phage", "phage", "sticky", "frag", "smoke", "emp"] as const;

export class Game {
  readonly world: World;
  /** Level id this client built (URL `?level=`; the room's Welcome must agree or the client travels). */
  readonly levelId: string;
  /** Local player. Offline: created at construction. Online: created on Welcome; a placeholder until then. */
  player: PlayerState;
  net: NetClient | null = null;
  private netConfig: NetConfig | null = null;
  /** Online: true once the first authoritative local state arrived; prediction starts then. */
  synced = false;
  /** where a walk in through a city gate stands (Stage 697), until the first link has used it */
  private arrival: SpawnPoint | null = null;
  /** the room's sheet mutator (an Audit's), applied on top of every loadout the room admits (Stage 703) */
  private roomSheet: Parameters<World["setLoadout"]>[2] = undefined;
  readonly netStats = { corrections: 0, maxCorrectionM: 0, replayedInputs: 0, serverHitsOnMe: 0, myHits: 0, myShotsConfirmed: 0, log: [] as { tick: number; corr: number; ack: number; pendingBefore: number; replayed: number; wasAlive: boolean; stance: string }[] };
  readonly input: InputController;
  /** touch device: thumbs instead of pointer lock, and a frame a phone can hold (Stage 32) */
  readonly mobile: boolean;
  readonly touch: TouchControls | null = null;
  /** the frame monitor (Stage 50): only with `?perf=1`; null costs nothing */
  readonly perf: PerfMonitor | null = null;
  readonly renderer: Renderer;
  readonly hud: Hud;
  /** the WORLD MAP (Stage 705): the MAP tab in the city, null anywhere else */
  readonly worldMap: WorldMap | null = null;
  readonly file: GhostFile;
  readonly audio = new GameAudio();
  /** the player's settings (client/settings.ts), applied live */
  settings: Settings = loadSettings();
  /** THE RUN (Stage 14): `?mode=run` offline, or the room's Welcome mode "run" online; the last view */
  runMode = false;
  runView: (RunView & { today: number; cap: number; owed: number }) | null = null;
  runLog: string[] = [];
  /** the pause menu's hook: set by main when the menu flow is on */
  onLockLost: (() => void) | null = null;
  private lowHealthOn = false;
  private bot: Bot | null = null;
  private prev: Snapshot;
  private cur: Snapshot;
  private acc = 0;
  private last = -1;
  /** wall time of the last drawn frame: render-side motion (crowds, tram, tickers) advances by this, not by sim time */
  private lastRenderAt = -1;
  /** When false the rAF loop only renders; the sim advances via advance(). `?headless=1` starts paused. */
  realtime = !new URLSearchParams(location.search).has("headless");
  /** When false, the loop simulates but skips drawing (probes on software GL). */
  drawing = !new URLSearchParams(location.search).has("norender");
  hitmarkers = false;
  /** the trigger pull that produced this tick's shots, and whether it was the alt (Stage 94) */
  private altFire = { tick: -1, alt: false };
  /** the last round this client landed on each body, so a close can name what closed it (Stage 91) */
  private closeBook = new Map<string, LandedHit>();
  readonly recentEvents: SimEvent[] = [];
  readonly stats = { ticks: 0, frames: 0, droppedTime: 0, fps: 0, simHz: 0, wallStart: 0, realtimeWall: 0, realtimeTicks: 0, maxFrameDt: 0, catchupHits: 0 };
  private stepDist = 0;
  private stepSide = 1;
  /** City soundscape schedule (sim ticks, so headless probes hear the same city): next siren, next PA line, PA index. */
  private city = { nextSiren: 0, nextPa: 0, paIndex: 0, sirenSide: 1 };
  /** One alert when a car comes into hail, and one when the file is in it. */
  private rideHint = false;
  private rideOffHint = false;
  /** Every PA line the city has spoken this session (probe-readable). */
  readonly cityLog: string[] = [];
  /** Identity & rituals: social messages received, and the room id of the file I owe a Debt to (−1 none). */
  readonly socialLog: SocialMsg[] = [];
  debtTargetId = -1;
  /** the range ghost recorder/player (hub only) */
  ghost: RangeGhost | null = null;
  /** the campaign (Stage 10): contracts, missions, dialogue, Threat, protocols */
  readonly campaign: Campaign;
  ghostPose: { x: number; y: number; z: number; yaw: number } | null = null;
  private fpsWindow: PerfWindow = perfWindow();

  constructor(canvas: HTMLCanvasElement, hudRoot: HTMLElement) {
    const q = new URLSearchParams(location.search);
    this.levelId = LEVEL_IDS.includes(q.get("level") ?? "") ? q.get("level")! : DEFAULT_LEVEL_ID;
    // a contract or an explorable district runs without the wake and without dummy respawns (targets stay down)
    const campaignMode = q.has("mission") || q.get("explore") === "1" || q.get("mode") === "campaign";
    this.runMode = q.get("mode") === "run";
    // the city (Stage 692) is the campaign's shared open world: no match, the patrols live, no player can hurt another
    const city = inCity(q);
    this.world = new World(levelById(this.levelId), { ai: q.get("ai") !== "0", seed: Number(q.get("seed") ?? 1) || 1, wakePhase: q.get("wake") === "0" || campaignMode ? "off" : "wake", dummyRespawn: !campaignMode || city, run: this.runMode, pvp: !city });
    this.audio.tune(this.world.level.name);
    if (city) {
      this.world.contestAt = (x, z) => inContest(this.world.level, x, z);
      this.world.contestGate = (x, z) => contestRespawn(this.world.level, x, z);
    }
    this.file = new GhostFile(() => this.online);
    this.player = this.world.addPlayer(1, "BLANK", 1, this.file.localLoadout());
    // a walk in through a city gate (Stage 697) stands at that gate, facing in: here until the room
    // answers, and the room puts the file in the same place (it checks the gate; the page cannot choose)
    this.arrival = city ? arrivalFromQuery(this.world.level, q) : null;
    if (this.arrival) reviveMotion(this.player, this.arrival);
    this.input = new InputController(canvas);
    this.input.yaw = this.player.yaw;
    // A phone has no pointer lock, no keyboard and no mouse, so it gets thumbs and a cheaper frame
    // (Stage 32). Both decided once, here, from the same answer.
    this.mobile = wantsTouch();
    // The HUD markup reads this class. It has to be on before the receipt's sign line is built.
    if (this.mobile) hudRoot.classList.add("touch");
    this.renderer = new Renderer(canvas, this.world.level, undefined, this.mobile, city);
    if (city) this.renderer.campaignFx.setContest(contestOf(this.world.level));
    this.hud = new Hud(hudRoot);
    this.hud.receiptTap = () => this.sign();
    if (this.mobile) {
      this.touch = new TouchControls(hudRoot);
      if (new URLSearchParams(location.search).get("touch") === "1") this.touch.root.classList.add("forced");
      this.input.touch = this.touch;
      this.input.aimAssist = (yaw, pitch) => this.touchAimAssist(yaw, pitch);
      this.touch.onGesture = () => {
        this.audio.resume();
        this.hud.setLocked(true);
      };
    }
    {
      const q = new URLSearchParams(location.search);
      if (q.get("perf") === "1") this.perf = new PerfMonitor(this, hudRoot, Number(q.get("perfAfter") ?? 30) || 30);
    }
    this.hud.setLevel(this.world.level, (id) => this.travel(id));
    // in the city the MAP tab is the whole city (Stage 705): its districts, who is where, and a trip
    // to another district's city room — not the offline level travel of the district select
    if (city) {
      const nonav = q.get("nonav") === "1";
      const map = new WorldMap(hudRoot, this.levelId, this.mobile, (url) => {
        this.renderer.post.kick(1);
        travelTo(url, loadingFor(url, { line: `THE CITY · ACROSS THE MAP FROM ${placeName(this.levelId)}` }), { replace: true, delay: 120, nonav });
      }, undefined, (url) => {
        this.renderer.post.kick(1);
        travelTo(url, loadingFor(url), { replace: true, delay: 120, nonav });
      });
      (this as { worldMap: WorldMap | null }).worldMap = map;
      this.hud.mapToggle = () => map.toggle();
    }
    this.file.mount(hudRoot);
    // the MARKET tab was a label. It opens the same panel Tab opens from a kiosk, on the market.
    this.hud.marketToggle = () => this.file.toggle(true, "market");
    // the booth's name line is the name desk, not the listings
    this.hud.gateTap = () => this.file.toggle(true, "name");
    // the NAME tab is how a phone opens the desk. N still opens it.
    this.hud.nameToggle = () => this.file.toggle(true, "name");
    this.hud.setFile(this.file.view());
    this.file.onChange = (f) => {
      // offline the loadout applies at once; online the server decides at the next link
      if (!this.online) {
        this.world.setLoadout(this.player, f.localLoadout());
        this.campaign?.keepEdge();
      }
      this.hud.setFile(f.view());
      this.applyMastery();
      this.refreshHub();
    };
    this.file.onStamp = (lines, ranks, challenges) => {
      // a first, verified by the server: the CRT stutters and the line commits
      for (const l of lines) {
        this.hud.push(`STAMP · ${l}`, "am");
        this.hud.alert(`◆ ATTESTED — ${l}`, true, 3.5);
      }
      for (const r of ranks) this.hud.push(`MASTERY · ${masteryRankLine(r)}`, "cy");
      for (const c of challenges) this.hud.push(`CHALLENGE CLEARED · ${challengeClearedLine(c)}`, "cy");
      if (lines.length || ranks.length) this.renderer.post.kick(0.6);
    };
    this.file.onIdentity = (f) => this.applyIdentity(f.identityView());
    // the look (Stage 689): the local body wears it from the start and is recut when the CHARACTER page changes it
    this.renderer.setLook(this.file.look);
    this.file.onLook = (code) => this.renderer.setLook(code);
    // from a safe zone's kiosk, Tab opens the panel on the market (offline and online alike)
    this.file.openSection = () => (this.runView?.inSafe || this.campaign.atLedgerDesk ? "market" : "top");
    this.applyIdentity(this.file.identityView());
    this.campaign = new Campaign(this);
    // the CONTRACTS tab was a label. J still opens the desk; the tab does too.
    this.hud.contractsToggle = () => this.campaign.toggleContracts();
    this.file.onEndgame = (f) => {
      this.hud.setTheme(f.themePalette());
      this.hud.setSeason(f.endgame.season);
    };
    this.file.onJoinAudit = () => this.joinAudit();
    // the contracts panel's clicks
    hudRoot.querySelector(".contracts")?.addEventListener("click", (e) => {
      // [data-crew] before [data-launch]: the crew span sits inside the launch row, and closest() stops at the first match on the way up
      const t = (e.target as HTMLElement).closest("[data-crew],[data-act],[data-launch],[data-wear],[data-explore]") as HTMLElement | null;
      if (t) this.campaign.onPanelAction(t);
    });
    hudRoot.querySelector(".contracts")?.addEventListener("change", (e) => {
      const t = e.target as HTMLElement;
      if (t.dataset.wear !== undefined) this.campaign.onPanelAction(t);
    });
    // the campaign starts once the file is known: at once offline, after the ledger host answers with ?shop=
    if (this.file.shop && !this.online) this.file.onLoaded = () => this.campaign.start();
    else this.campaign.start();
    this.file.onChange = ((prev) => (f: GhostFile) => {
      prev?.(f);
      this.hud.setTheme(f.themePalette());
    })(this.file.onChange);
    if (this.world.level.hub) {
      this.ghost = new RangeGhost(this.world.level.hub, this.levelId, `meltdown.ghost.${this.file.account}.${this.levelId}`);
      this.ghost.onFinish = (run, improved) => {
        this.hud.push(`RANGE · ${run.seconds.toFixed(2)}S${improved ? " · NEW BEST" : ` · BEST ${this.ghost!.best!.seconds.toFixed(2)}S`}`, improved ? "am" : "k");
        this.hud.alert(improved ? `◆ RANGE RECORD — ${run.seconds.toFixed(2)}S` : `◆ RANGE — ${run.seconds.toFixed(2)}S`, !improved, 3);
        if (improved) {
          this.audio.sign(this.world.level.name);
          void this.file.postGhost(run);
        }
      };
      this.refreshHub();
    }
    this.hud.onPrint = () => this.audio.printTick(this.world.level.name);
    this.hud.onStamp = () => this.audio.sign(this.world.level.name);
    document.addEventListener("keydown", (e) => {
      if (e.code === "Enter" || e.code === "NumpadEnter") this.sign();
    });
    this.prev = snap(this.player);
    this.cur = snap(this.player);
    this.input.onGesture = () => this.audio.resume();
    // Any gesture anywhere wakes the audio, not only one that reaches the canvas (Stage 177). The
    // opening crawl's overlay sits over the canvas and calls stopPropagation() on its own clicks,
    // so for the whole crawl — and for the menu after a keyboard exit from it — nothing reached
    // the canvas listener and no AudioContext was ever built: the hum, the key per two characters
    // and the tear all counted themselves and returned at `if (!this.ctx) return`. Capture phase,
    // because the overlay stops the bubble. Not `once`: `resume()` is idempotent and also lifts a
    // context the browser suspended while the tab was in the background.
    for (const ev of ["pointerdown", "keydown", "touchstart"] as const) document.addEventListener(ev, () => this.audio.resume(), { capture: true });
    this.applySettings(this.settings);
    document.addEventListener("visibilitychange", () => this.audio.duck(document.hidden));
    this.input.onLockChange = (l) => {
      this.hud.setLocked(l);
      if (!l) this.onLockLost?.(); // the pause menu, when the menu flow is on
    };
    this.renderer.syncDummies(this.world.dummies);
  }

  get online(): boolean {
    return this.net !== null;
  }

  private applyIdentity(v: ReturnType<GhostFile["identityView"]>): void {
    this.hud.setIdentity(v.glyphSvg, v.display, v.monikerText, v.chapter);
    const worn = skinByToken(this.file.accountRecord?.counter?.worn ?? 0);
    this.renderer.setSkin(worn?.tint ?? null, worn?.texture ?? null);
    this.applyMastery();
    this.refreshHub();
  }

  /** A weapon at the mastery cap carries its finish, in first person and in the hand (Stage 675). */
  private applyMastery(): void {
    for (const w of WEAPON_LIST) this.renderer.setMastered(w.id, (this.file.mastery[w.id]?.rank ?? 1) >= MAX_RANK);
  }

  /** The office renovates with the Chapter; the trophy wall is cut from the file's ledger; the ghost adopts the file's best run. */
  private refreshHub(): void {
    if (!this.renderer.hub) return;
    const v = this.file.identityView();
    this.renderer.hub.set({ chapter: v.chapter, named: v.chapter >= 3 ? v.display : null, trophies: trophiesFromLedger(this.file.ledger), visitor: this.campaign ? officeVisitor(this.campaign.current()) : null });
    this.ghost?.adopt(this.file.ghosts[this.levelId]);
  }

  /** Travel into this week's Audit room on the linked ledger host (a plain wake elsewhere becomes the playlist). */
  joinAudit(): void {
    const shop = this.file.shop;
    const au = this.file.endgame.audit;
    if (!shop || !au) return;
    const ws = shop.replace(/^http/, "ws");
    const u = new URL(location.href);
    u.searchParams.set("net", `${ws}/room/audit-${au.week}?audit=1&level=${this.levelId}`);
    u.searchParams.delete("mission");
    u.searchParams.delete("explore");
    this.renderer.post.kick(1);
    travelTo(u.toString(), loadingFor(u.toString()), { replace: true, delay: 120 });
  }

  /** Sign the post-match Ledger Entry (Enter). The receipt must have finished printing. */
  sign(): boolean {
    const ok = this.hud.sign();
    if (ok) {
      this.audio.sign(this.world.level.name);
      this.hud.push("LEDGER ENTRY SIGNED", "am");
    }
    return ok;
  }

  /** Kill-confirm layers follow the shooter's own mastery tier for the weapon (rank 1–9 → 0 … 30 → 3). */
  private killTier(weapon: string): number {
    const r = this.file.mastery[weapon]?.rank ?? 1;
    return Math.min(3, Math.floor(r / 10));
  }

  private onSocial(m: SocialMsg): void {
    this.socialLog.push(m);
    if (this.socialLog.length > 64) this.socialLog.shift();
    const svgFor = (seed: number, chapter: number) => {
      const g = glyphFor(String(seed), chapter >= 3 ? 50 : chapter >= 2 ? 25 : chapter >= 1 ? 10 : 1);
      g.seed = seed;
      return glyphSvg(g, 26, "#35f2ff");
    };
    switch (m.kind) {
      case "dossier": {
        const me = this.player.id;
        this.hud.dossier(m.entries.map((e) => ({ team: e.team, display: e.display, glyphSvg: svgFor(e.glyph, e.chapter), chapter: e.chapter, moniker: monikerById(e.moniker)?.text ?? null, stamps: e.stamps, debt: e.debt, me: e.id === me })), m.seconds);
        this.audio.dossier();
        const target = m.entries.find((e) => e.debt);
        this.debtTargetId = target ? target.id : -1;
        break;
      }
      case "debt":
        if (m.event === "owed") {
          this.debtTargetId = m.id;
          this.hud.debt("owed", m.display, filesWord(m.kills));
          this.audio.debtOwed(this.world.level.name);
          this.hud.push(`DEBT · ${m.display} · ${filesWord(m.kills)} ON YOU`, "mg");
        } else {
          this.debtTargetId = -1;
          this.hud.debt("cleared", m.display, m.capped ? "CAPPED" : `+${m.credit} WAKELIGHT`);
          this.audio.debtCleared();
          this.renderer.post.kick(1);
          this.hud.push(`DEBT CLEARED · ${m.display}${m.capped ? " · CAPPED" : ` · +${m.credit} WAKELIGHT`}`, "am");
        }
        break;
      case "rite":
        this.hud.rite(m.numeral, m.title, m.lines, 5);
        this.audio.rite(m.chapter);
        this.renderer.post.kick(1);
        this.hud.push(`CHAPTER ${m.numeral} · ${m.title}${m.named ? ` · THE CITY CALLS YOU ${m.display}` : ""}`, "am");
        break;
    }
  }

  /** Connect to a room. The offline world is replaced by a server-fed one. */
  connect(cfg: NetConfig): void {
    this.netConfig = cfg;
    this.synced = false;
    this.world.removePlayer(this.player.id);
    const inner = new WsTransport(cfg.url);
    const transport = cfg.sim ? new SimulatedLink(inner, cfg.sim) : inner;
    const net = new NetClient(transport, cfg.name, cfg.token ?? "", this.file.account, this.file.loadoutJson(), this.file.identityJson(), this.file.secret);
    this.net = net;
    net.onSocial = (m) => this.onSocial(m);
    net.onMission = (m) => this.campaign.onMissionMsg(m);
    net.onCityEvent = (m) => this.campaign.onCityEventMsg(m);
    net.onCityRun = (m) => this.campaign.onCityRunMsg(m);
    net.onRun = (m) => this.onRunMsg(m);
    net.onFile = (f) => {
      this.file.applyServer(f);
      for (const line of f.ledger) if (line.startsWith("SCRIP · BANKED")) this.hud.alert(`◆ ${line}`, false, 3);
      if (f.reason === "join" && this.net === net) {
        // the server admitted this loadout: run the same sheet locally (arrives before the first snapshot),
        // with the room's own mutator on top — the File comes after the Welcome that named it, and
        // setting the loadout bare here undid an Audit's sheet (a GLASS week predicted shields and ×1
        // movement the room did not run; Stage 703)
        this.world.setLoadout(this.player, f.loadout as Parameters<World["setLoadout"]>[1], this.roomSheet);
        this.hud.push(`FILE ${f.account} · DEPTH ${String(f.depth).padStart(2, "0")} · ATTESTED [${f.loadout.attested.map(itemName).join(", ") || "NONE"}]${f.loadout.keystone ? " · " + itemName(f.loadout.keystone) : ""}`, "cy");
      } else if (f.reason === "settle") {
        // the Ledger Entry ritual: the receipt prints line by line, the stamp thunks, the player signs
        this.hud.receipt(f.ledger);
        // the totals line by its prefix, not by its index: the receipt grew two lines in Stage 29
        // and an index would have gone on working while showing the wrong one
        this.hud.alert(`◆ LEDGER SETTLED — ${f.ledger.find((l) => l.startsWith("XP +")) ?? ""}`, false, 5);
      }
    };
    net.onStatus = (st) => {
      if (st === "joined") {
        if (net.levelName && net.levelName !== this.levelId && LEVEL_IDS.includes(net.levelName)) {
          // the room plays a different district: travel there (a fresh world and renderer for that level)
          this.hud.alert(`◆ TRAVELLING — ${levelDisplayName(net.levelName)}`, false, 3);
          this.travel(net.levelName, "THE ROOM PLAYS HERE · TRAVELLING");
          return;
        }
        (this.world as { seed: number }).seed = net.seed;
        this.player = this.world.addPlayer(net.playerId, cfg.name, 1, this.file.admitted ?? this.file.localLoadout());
        // a private room (Stage 20): the buyer's district, mode and clock — and no $CAPITAL
        const priv = net.mode.startsWith("private:");
        const mode = priv ? net.mode.slice("private:".length) : net.mode;
        if (priv) this.hud.push("PRIVATE ROOM · THE BUYER'S RULES AND INVITE LIST · BANKS SCRIP, NEVER $CAPITAL", "am");
        // an Audit room: the same symmetric rules the room runs, so prediction agrees
        if (mode === "run") {
          this.runMode = true;
          this.hud.push("PLAY TO EARN · CARRY $CAPITAL CLAIMS TO A GATE; DIE AND THEY DROP", "am");
        }
        const am = mode.match(/^audit:([a-z_]+):(\d+)$/);
        const audit = am ? AUDITS.find((x) => x.id === am[1]) : undefined;
        if (audit) {
          this.world.gravityMult = audit.gravityMult;
          if (Object.keys(audit.sheet).length) {
            this.roomSheet = audit.sheet;
            this.world.setLoadout(this.player, this.file.admitted ?? this.file.localLoadout(), audit.sheet);
          }
          this.hud.push(`AUDIT · ${audit.name} · ${audit.line}`, "am");
        }
        // the first link after a walk through a city gate faces in from it, as the room placed it
        // (Stage 697); a relink keeps the seat, and the snapshot says where that is
        if (this.arrival) reviveMotion(this.player, this.arrival);
        this.arrival = null;
        this.input.yaw = this.player.yaw;
        this.hud.push(`${this.rejoins > 0 ? "RELINKED" : "LINKED"} · ROOM ${roomName(cfg.url)} · FILE #${net.playerId}`, "cy");
        this.rejoins = 0;
      } else {
        this.hud.push(linkStatusLine(st, net.kickReason), "mg");
        // a drop is not a departure (Stage 153): the room keeps the seat for its grace window, so
        // knock, on a doubling wait, for as long as it is held. A kick or a close of our own does
        // not knock. A handshake that never completed knocks too (Stage 155): it has no token to
        // rejoin with, so it asks the door again from the start rather than ending there
        if (st === "closed" && !net.left && this.netConfig) this.knock();
      }
    };
    net.onSnapshot = (snap) => this.onSnapshot(snap);
    this.hud.push(`LINKING ${cfg.url}${cfg.sim ? linkingSimNote(cfg.sim) : ""}`, "k");
  }

  /** Travel to another district: the world and renderer are built per level, so the page reloads with `?level=`, behind the loading card. */
  travel(levelId: string, line?: string): void {
    if (!LEVEL_IDS.includes(levelId) || levelId === this.levelId) return;
    const u = new URL(location.href);
    u.searchParams.set("level", levelId);
    // the apartment, the pit, the limit, and the preserve are not city rooms: a street socket would pull the page back to the district
    if (levelId === FILE_APARTMENT_ID || this.levelId === FILE_APARTMENT_ID || levelId === SCRIP_PIT_ID || this.levelId === SCRIP_PIT_ID || levelId === CITY_LIMIT_ID || this.levelId === CITY_LIMIT_ID || levelId === GREEN_HOLD_ID || this.levelId === GREEN_HOLD_ID) {
      for (const k of ["net", "city", "token", "mission", "mode", "explore", "from", "gate", "job", "back"]) u.searchParams.delete(k);
    } else if (this.net?.token) u.searchParams.set("token", this.net.token);
    this.renderer.post.kick(1);
    travelTo(u.toString(), loadingFor(u.toString(), { line }), { replace: true, delay: 120 });
  }

  /** how many times this drop has been knocked on, cleared when the room takes the file back */
  private rejoins = 0;
  private knockTimer: ReturnType<typeof setTimeout> | null = null;

  /** Knock on a room that still holds the seat (Stage 153). */
  private knock(): void {
    if (this.knockTimer !== null) return;
    const wait = rejoinDelay(this.rejoins + 1);
    if (wait === null) {
      this.hud.push(`LINK LOST · THE ROOM HAS LET THE SEAT GO AFTER ${triesWord(this.rejoins)}`, "mg");
      return;
    }
    this.rejoins++;
    this.hud.push(`LINK LOST · REJOINING IN ${(wait / 1000).toFixed(1)}S · TRY ${this.rejoins} OF ${rejoinTries()}`, "am");
    this.knockTimer = setTimeout(() => {
      this.knockTimer = null;
      this.reconnect();
    }, wait);
  }

  /** Rejoin the same room with the session token (state is restored server-side). */
  reconnect(): void {
    if (!this.netConfig || !this.net) return;
    const token = this.net.token;
    this.net.close();
    this.connect({ ...this.netConfig, token });
  }

  private onSnapshot(ns: NetSnapshot): void {
    const net = this.net!;
    if (net.status !== "joined") return;
    const p = this.player;
    // dummies are server-driven online
    for (const d of ns.dummies) {
      const local = this.world.dummies.find((x) => x.id === d.id);
      if (!local) continue;
      local.alive = d.alive;
      local.health = d.health;
      local.pos.x = d.x;
      local.pos.y = d.y;
      local.pos.z = d.z;
    }
    // one the room did not send is out of this client's view (a city's interest, Stage 706): it is not
    // drawn, rather than left standing where it was last told of. Every other room sends them all
    if (ns.dummies.length < this.world.dummies.length) for (const local of this.world.dummies) if (!ns.dummies.some((d) => d.id === local.id)) local.alive = false;
    if (ns.local) {
      const before = { x: p.pos.x, y: p.pos.y, z: p.pos.z };
      const wasSynced = this.synced;
      const wasAlive = p.alive;
      const pendingBefore = net.pendingInputs.length;
      const yawBefore = p.yaw;
      const velBefore = [+p.vel.x.toFixed(2), +p.vel.z.toFixed(2)];
      const lastIn = net.pendingInputs[net.pendingInputs.length - 1];
      this.world.importLocal(p, ns.local);
      const replay = net.ackUpTo(ns.local.seq);
      for (const r of replay) this.world.applyInput(p, r, { predictOnly: true, silent: true });
      this.netStats.replayedInputs += replay.length;
      // online never steps the world, so the sim's respawn event never fires here. The dead→alive
      // edge on the snapshot is the same moment (Stage 191).
      if (!wasAlive && p.alive) this.backOnTheLedger();
      if (wasSynced && wasAlive && p.alive) {
        const corr = Math.hypot(p.pos.x - before.x, p.pos.y - before.y, p.pos.z - before.z);
        if (corr > 0.001) this.netStats.corrections++;
        if (corr > this.netStats.maxCorrectionM) this.netStats.maxCorrectionM = corr;
        if (corr > 0.02 && this.netStats.log.length < 12) this.netStats.log.push({ tick: ns.tick, corr, ack: ns.local.seq, pendingBefore, replayed: replay.length, wasAlive, stance: p.stance, yawBefore: +yawBefore.toFixed(4), yawAfter: +p.yaw.toFixed(4), yawSrv: +ns.local.yaw.toFixed(4), yawIn: lastIn ? +lastIn.yaw.toFixed(4) : 0, velBefore, velAfter: [+p.vel.x.toFixed(2), +p.vel.z.toFixed(2)] } as never);
      }
      this.synced = true;
      this.prev = this.cur = snap(p);
      this.world.tick = ns.tick;
    }
    this.netEntities = ns.entities;
    this.netMatch = ns.match;
    for (const ev of ns.events) this.onNetEvent(ev);
  }

  private onNetEvent(ev: NetSnapshot["events"][number]): void {
    const me = this.player.id;
    switch (ev.type) {
      case "shot": {
        const hit = ev.hitKind >= 2;
        const def = WEAPON_LIST.find((w) => WEAPON_WIRE[w.id] === ev.weapon) ?? WEAPON_LIST[ev.weapon - 1];
        const color = def?.tracer ?? 0xffb02e;
        // every shot in the room, not only mine (Stage 89): a round into a body used to end in
        // mid-air for everyone watching. The wire does not carry the damage, so it is worked out
        // here from the weapon, the zone and the distance the shot actually flew — the simulation's
        // own arithmetic, in `client/hit.ts`
        if (hit) {
          const kind = ev.hitKind === 2 ? "dummy" : ev.hitKind === 3 ? "player" : ev.hitKind === 4 ? "wasp" : "mech";
          const distance = Math.hypot(ev.tx - ev.fx, ev.ty - ev.fy, ev.tz - ev.fz);
          const zone = kind === "dummy" || kind === "player" ? (ev.zone ?? "body") : "body";
          const damage = landedDamage(def ?? WASP_SHOT, zone, distance, kind === "mech" ? 0.5 : 1);
          this.renderer.hitBody(kind, ev.victimId, { x: ev.tx, y: ev.ty, z: ev.tz }, impactRead(damage), lookYawPitch({ x: ev.tx, y: ev.ty, z: ev.tz }, { x: ev.fx, y: ev.fy, z: ev.fz }).yaw, color);
          // and if it was mine, it is a candidate for having closed that body (Stage 91)
          if (ev.playerId === me) this.landed(bodyKey(kind, ev.victimId), { at: this.renderer.clockNow, zone, distance, weapon: def?.id ?? "wasp" });
        }
        if (ev.playerId === me) {
          this.netStats.myShotsConfirmed++;
          if (hit) {
            this.netStats.myHits++;
            this.audio.hit(ev.zone ?? "body", this.world.level.name);
            if (this.hitmarkers) this.hud.flashHit();
          }
        } else {
          if (ev.weapon === 4) this.renderer.fx.beam({ x: ev.fx, y: ev.fy, z: ev.fz }, { x: ev.tx, y: ev.ty, z: ev.tz }, color, 0.05, 0.6);
          else {
            this.renderer.tracer({ x: ev.fx, y: ev.fy, z: ev.fz }, { x: ev.tx, y: ev.ty, z: ev.tz }, ev.hitKind === 1, true, color);
            this.renderer.kickRemote(ev.playerId);
          }
          // and it is heard from where it was fired (Stage 81). Every shot in the room used to
          // arrive at the same volume from nowhere in particular: a rail two districts wide of you
          // sounded exactly like one at your shoulder, which is worse than silence for working out
          // where the danger is.
          const cue = gunCue(ev.fx, ev.fz, this.listenPoint(), this.world.level.name);
          if (cue) {
            this.audio.otherShot(ev.weapon === 0 ? "wasp" : (def?.id ?? "lease_breaker"), cue, this.world.level.name);
            this.heardShot.n++;
            this.heardShot.pan = cue.pan;
            this.heardShot.gain = cue.gain;
            this.heardShot.delay = cue.delay;
            this.heardShot.distance = cue.distance;
            // and the map marks where it went off (Stage 104)
            this.pings = rememberPing(this.pings, { x: ev.fx, z: ev.fz, at: this.hud.mapClock, kind: ev.weapon === 0 ? "wasp" : "file" });
          }
          // and if it went past the head, the round itself is heard where it was nearest (Stage 99)
          this.passedBy({ x: ev.fx, y: ev.fy, z: ev.fz }, { x: ev.tx, y: ev.ty, z: ev.tz }, ev.hitKind === 3 && ev.victimId === me);
          if (ev.hitKind === 3 && ev.victimId === me) {
            this.netStats.serverHitsOnMe++;
            this.audio.hurt(this.world.level.name);
            this.hud.alert("▲ INTEGRITY BREACH", true, 1);
          }
        }
        break;
      }
      case "fx": {
        const pos = { x: ev.x, y: ev.y, z: ev.z };
        switch (ev.kind) {
          case FX.explode:
            this.renderer.fx.explosion(pos, ev.a / 10, ev.b === 3 ? 0xffb02e : 0x8f4dff, ev.b === 3);
            this.audio.explosion(ev.b === 3, this.world.level.name);
            if (Math.hypot(ev.x - this.player.pos.x, ev.z - this.player.pos.z) < (ev.a / 10) * 2) this.renderer.post.kick(0.6);
            break;
          case FX.cloud:
            this.audio.smoke(this.world.level.name);
            break;
          case FX.emp:
            this.audio.emp(this.world.level.name);
            this.renderer.fx.explosion(pos, ev.a / 10, 0x35f2ff, false);
            if (Math.hypot(ev.x - this.player.pos.x, ev.z - this.player.pos.z) < ev.a / 10) this.renderer.post.kick(1);
            break;
          case FX.flagged:
            if (ev.playerId === me) this.mechHasYou();
            break;
          case FX.stun:
            if (ev.playerId === me) {
              this.audio.stun(this.world.level.name);
              this.renderer.post.kick(0.8);
            }
            break;
          case FX.mechBeam:
            this.renderer.fx.beam({ x: ev.x, y: ev.y + 2.5, z: ev.z }, pos, 0xffb02e, 0.08, 0.35);
            if (ev.playerId === me) this.audio.mechBeam(this.world.level.name);
            break;
          case FX.hurt:
            if (ev.playerId === me) {
              this.audio.hurt(this.world.level.name);
              this.hud.alert(`▲ INTEGRITY −${ev.a}`, true, 0.8);
              // the hurt effect carries the attacker's position (Stage 74); all zeros means the room
              // could not name one — a fall, a hazard — and there is no direction to point at
              if (ev.x !== 0 || ev.y !== 0 || ev.z !== 0) this.tookHit(ev.x, ev.z, ev.a);
            }
            break;
          case FX.swap:
            // prediction already played this for us (Stage 198)
            if (ev.playerId !== me) this.audio.swap(this.world.level.name);
            break;
          case FX.melee: {
            const swing = meleeOfWire(ev.b);
            if (ev.playerId !== me) this.audio.shot(swing.weapon, swing.lunge, this.world.level.name);
            if (ev.a > 0) {
              this.audio.hit("body", this.world.level.name);
              const color = WEAPONS[swing.weapon as keyof typeof WEAPONS]?.tracer ?? 0xff3ec9;
              this.renderer.hitBody("mech", 0, { x: ev.x, y: ev.y, z: ev.z }, impactRead(34), 0, color);
            }
            break;
          }
          case FX.throw:
            if (ev.playerId !== me) this.audio.throw(this.world.level.name);
            break;
          case FX.chargeFull:
            if (ev.playerId !== me) this.audio.charge(1, this.world.level.name);
            break;
          case FX.lunge:
            if (ev.playerId !== me) this.audio.jump(this.world.level.name);
            break;
          case FX.waspDeath:
            this.hud.push(`WASP-${String(ev.a).padStart(2, "0")} DOWNED`, "am");
            this.audio.explosion(false, this.world.level.name);
            break;
          case FX.mechDeath:
            this.hud.push(`REPO MECH ${ev.a} DISABLED — VANTAGE RE-LEASING`, "am");
            this.audio.explosion(true, this.world.level.name);
            this.renderer.post.kick(1);
            break;
          case FX.nodeFlip: {
            const n = this.netEntities.find((e) => e.kind === ENT_NODE && e.id === ev.a);
            if (n) this.renderer.wake.flip({ x: n.x, y: n.y, z: n.z }, ev.b);
            this.audio.nodeFlip(ev.b === this.player.team, this.world.level.name);
            this.renderer.post.kick(0.5);
            this.hud.push(`NODE ${["", "A", "B", "C", "D", "E"][ev.a] ?? ev.a} — CELL ${ev.b === 1 ? "ONE" : "TWO"}`, ev.b === 1 ? "gr" : "cy");
            break;
          }
          case FX.nodeContest:
            this.hud.alert(`◆ NODE ${["", "A", "B", "C", "D", "E"][ev.a] ?? ev.a} CONTESTED`, true, 1.5);
            this.audio.contest(this.world.level.name);
            break;
          case FX.kernelPulse:
            this.kernelMark = this.netMatch?.timeLeft ?? null;
            this.hud.alert(`◆ KERNEL PULSE — NODE ${["", "A", "B", "C", "D", "E"][ev.a] ?? ev.a} ${ev.b ? "RE-LEASED" : "DRAINED"}`, true, 2.5);
            this.audio.kernelPulse(this.world.level.name);
            this.renderer.post.kick(0.8);
            break;
          case FX.phase:
            this.kernelMark = ev.a === 1 ? (this.netMatch?.timeLeft ?? null) : null;
            this.hud.alert(ev.a === 1 ? wakeBeginsLine() : ev.a === 2 ? roundOverLine(ev.b, districtName(this.world.level)) : "◆ WARM-UP", false, 4);
            // online the phase had been silent (Stage 124): the same voices as offline
            if (ev.a === 1) this.audio.wakeBegins(this.world.level.name);
            else if (ev.a === 2) this.audio.roundOver(ev.b === 0 ? "none" : ev.b === this.player.team ? "won" : "lost", this.world.level.name);
            else this.audio.kernelPulse(this.world.level.name);
            this.renderer.post.kick(1);
            break;
          case FX.fullWake:
            this.hud.alert(fullWakeLine(districtName(this.world.level)), false, 5);
            this.audio.nodeFlip(true, this.world.level.name);
            this.renderer.post.kick(1);
            break;
          default:
            break;
        }
        break;
      }
      case "kill": {
        const downed = (["dummy", "player", "wasp", "mech"] as const)[ev.victimKind];
        if (downed) this.campaign?.noteStreetKill(downed);
        if (ev.playerId === me) {
          // shooter-side: the tier follows the weapon in hand at the confirm (the file's own mastery; nothing leaves the client)
          this.audio.kill(this.killTier(weaponDefOf(this.player).id), this.world.level.name);
          const kind = ["dummy", "player", "wasp", "mech"][ev.victimKind] ?? "player";
          this.hud.killStamp(kind, closeLine(closeRead(this.closeBook, bodyKey(kind, ev.victimId), this.renderer.clockNow)));
          this.renderer.post.kick(1);
          this.hud.push(`FILE #${me} ⟶ ${victimLabel(kind)}-${String(ev.victimId).padStart(2, "0")}${ev.ttkTicks ? ttkNote(ev.ttkTicks / SIM_HZ) : ""}`, "mg");
        } else this.hud.push(`FILE #${ev.playerId} ⟶ ${victimLabel(["dummy", "player", "wasp", "mech"][ev.victimKind] ?? "player")}-${String(ev.victimId).padStart(2, "0")}`, "k");
        break;
      }
      case "death":
        if (ev.playerId === me) this.fileClosed(ev.killerId);
        break;
      case "join":
        this.hud.push(enteredLine(ev.playerId, ev.name, districtName(this.world.level)), "cy");
        break;
      case "leave":
        this.hud.push(`FILE #${ev.playerId} DROPPED OFF THE LEDGER`, "k");
        break;
    }
  }

  /** Touch aim assist (Stage 34). The rule and the reasoning live in `client/aimassist.ts`. */
  private touchAimAssist(yaw: number, pitch: number): number {
    const targets = this.botTargets();
    if (targets.length === 0) return 1;
    return aimAssistScale(eyePos(this.player), yaw, pitch, targets, (from, to) => canSee(from, to, this.world.level.boxes, this.world.clouds));
  }

  private botTargets(): BotTarget[] {
    if (!this.net) return [];
    return this.net.remoteViews().map((v) => ({ id: v.id, x: v.x, y: v.y, z: v.z, height: v.height, alive: v.alive }));
  }

  private lastFrameAt = 0;

  /** Settings, applied live: sensitivity is input, FOV and CRT the renderer, volumes the audio buses. Never the sim. */
  applySettings(s: Settings): void {
    this.settings = s;
    this.input.sensitivity = 0.0022 * s.sensitivity;
    if (this.touch) this.touch.sensitivity = s.sensitivity;
    this.renderer.setFov(s.fov);
    this.renderer.setCrt(s.crt);
    // the view is the trailer's — third person — unless the setting or the URL says first (Stage 60)
    const q = new URLSearchParams(location.search).get("view");
    this.renderer.setView(q === "first" ? false : q === "third" ? true : !s.firstPerson);
    this.audio.setVolumes({ master: s.master, sfx: s.sfx, bed: s.bed });
  }

  start(): void {
    this.stats.wallStart = performance.now();
    const loop = () => {
      // one clock for both drivers: rAF timestamps lag performance.now() under load and would double-count
      this.frame(performance.now(), true);
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
    // Hidden tabs get no animation frames; keep the simulation (and the net link) alive on a timer.
    setInterval(() => {
      const now = performance.now();
      if (now - this.lastFrameAt > 60) this.frame(now, false);
    }, 16);
  }

  /** Attach a scripted driver; passing null returns control to the keyboard. */
  setBot(plan: BotStep[] | null): void {
    this.bot = plan ? new Bot(plan, this.player.yaw) : null;
    this.hud.setLocked(!!plan || this.input.isLocked);
    if (!plan) {
      this.input.yaw = this.player.yaw;
      this.input.pitch = this.player.pitch;
    }
  }

  get botStatus(): { done: boolean; log: string[]; killed: boolean; current: BotStep | undefined; aimed: number } | null {
    // `aimed` is the bot's own tally of triggers pulled while a killPlayer step had the crosshair on
    // a target it could see. Against the server's count of rounds actually away it says which half
    // of "nothing was fired" happened: the driver never took the shot, or the shot never landed.
    return this.bot ? { done: this.bot.done, log: this.bot.log, killed: this.bot.killed, current: this.bot.current, aimed: this.bot.aimedShots } : null;
  }

  /** Run exactly n ticks now (deterministic; used by probes). */
  advance(n: number): void {
    for (let i = 0; i < n; i++) this.tick();
  }

  hash(): string {
    return hashWorld(this.world);
  }

  /** VANTAGE PA copy; `{D}` is the district name. */
  static readonly PA_LINES = [
    "VANTAGE ADVISES {D}: LEASE RENEWAL IS AUTOMATIC. THANK YOU FOR YOUR CONTINUED COMPLIANCE.",
    "CITIZENS OF {D}: CURFEW BEGINS WHEN THE KERNEL SAYS IT DOES.",
    "UNLISTED FILES DETECTED IN {D}. RE-LEASE CREWS DISPATCHED.",
    "REMINDER: SLEEP IS COLLATERAL. DREAM RESPONSIBLY.",
    "THE MONORAIL RUNS ON TIME. SO WILL YOU.",
    "STABILITY IS A SERVICE. YOUR SUBSCRIPTION IS CURRENT.",
    "REPORT UNLISTED NEIGHBOURS. GRATITUDE IS CREDITED.",
    "{D} INTEGRITY: NOMINAL. WAKE ACTIVITY: BEING PRICED.",
  ];

  /**
   * Draw the car the file is sitting in on the sim seat. The mesh otherwise runs on render time, which
   * is what the city probe measures while the tick stands still. Hold is applied at the end of that
   * update, so a frame with no rider still sees the city clock.
   */
  private syncTram(): void {
    const tram = this.renderer.life.tram;
    if (tram) tram.hold = null;
    const line = this.world.level.tram;
    if (!tram || !line || !this.player.alive) {
      this.rideHint = false;
      this.rideOffHint = false;
      return;
    }
    const feet = this.player.pos;
    const ticks = [this.world.tick, Math.max(0, this.world.tick - 1)];
    const pending = this.net?.pendingInputs;
    const last = pending && pending.length > 0 ? pending[pending.length - 1] : undefined;
    if (last) ticks.push(last.tick);
    let car: ReturnType<typeof tramAboard> = null;
    let best = Infinity;
    for (const tick of ticks) {
      const hit = tramAboard(line, tick * SIM_DT, feet);
      if (!hit) continue;
      const d = Math.hypot(hit.x - feet.x, hit.z - feet.z);
      if (d < best) {
        car = hit;
        best = d;
      }
    }
    if (car) {
      // The feet were snapped to this car. Pin the hull to them, not to a tick the render clock guessed.
      tram.hold = { dir: car.dir, x: feet.x, y: feet.y - TRAM_CABIN, z: feet.z };
      if (!this.rideOffHint) {
        this.hud.alert("LOOK AHEAD AND JUMP — STEP OFF", false, 2);
        this.rideOffHint = true;
      }
      this.rideHint = false;
      return;
    }
    this.rideOffHint = false;
    const hail = tramHail(line, this.world.tick * SIM_DT, feet) ?? tramHail(line, Math.max(0, this.world.tick - 1) * SIM_DT, feet);
    if (hail && !this.rideHint) {
      this.hud.alert("LOOK UP AND JUMP — RIDE THE CAR", false, 2);
      this.rideHint = true;
    }
    if (!hail) this.rideHint = false;
  }

  /** The city keeps talking whether or not you fight: sirens across the district and PA lines on a sim-tick schedule. */
  private cityTick(): void {
    const t = this.world.tick;
    const c = this.city;
    if (t === 0) {
      c.nextSiren = 9 * SIM_HZ;
      c.nextPa = 5 * SIM_HZ;
    }
    if (t >= c.nextSiren) {
      c.sirenSide = -c.sirenSide;
      this.audio.siren(0.6 * c.sirenSide, this.world.level.name);
      c.nextSiren = t + Math.round((sirenWait(this.world.level.name) + ((t * 7) % 23)) * SIM_HZ);
    }
    if (t >= c.nextPa) {
      const district = districtName(this.world.level);
      const named = this.campaign?.threat.named && c.paIndex % 2 === 1;
      const who = this.file.identityView().display;
      const line = named ? `VANTAGE ADVISES ${district}: ${who} IS UNLISTED. REPORT ON SIGHT. THREAT RATING ${this.campaign.threat.rating}.` : Game.PA_LINES[c.paIndex % Game.PA_LINES.length]!.replace(/\{D\}/g, district);
      c.paIndex++;
      this.audio.pa(this.world.level.name);
      this.cityLog.push(line);
      this.hud.push(`VANTAGE PA · ${line}`, "am pa"); // Stage 133: the one line of the log that wraps
      c.nextPa = t + Math.round((27 + ((c.paIndex * 11) % 17)) * SIM_HZ);
    }
  }

  private tick(): void {
    const t = this.world.tick;
    this.cityTick();
    this.player.edgeDrill = this.campaign.current().edgeDrill === true;
    if (this.net) {
      // Online: predict the local player only; the server owns everything else.
      if (this.net.status !== "joined" || !this.synced) return;
      const frame: InputFrame = this.bot ? this.bot.sample(this.world, this.player, t, this.botTargets()) : this.input.sample(t);
      // apply exactly what the wire carries (angles quantized to 1e-4 rad) so prediction and server agree bit for bit
      // wrap first: the wire's i16 cannot carry angles beyond ±3.2767 rad, and both sides must apply the same value
      frame.yaw = Math.round(wrapAngle(frame.yaw) * 10000) / 10000;
      frame.pitch = Math.round(Math.max(-1.55, Math.min(1.55, frame.pitch)) * 10000) / 10000;
      const ni: NetInput = { ...frame, seq: this.net.nextSeq(), viewTick: this.net.viewTick(), viewFrac: this.net.viewFrac(), px: 0, py: 0, pz: 0 };
      this.world.applyInput(this.player, ni, { predictOnly: true });
      ni.px = this.player.pos.x;
      ni.py = this.player.pos.y;
      ni.pz = this.player.pos.z;
      this.net.sendInput(ni);
      this.world.tick++;
    } else {
      let frame: InputFrame = this.bot ? this.bot.sample(this.world, this.player, t) : this.input.sample(t);
      // a terminal or the contracts desk has the keyboard: the Blank stands still
      if (this.campaign?.uiOpen && !this.bot) frame = { ...emptyInput(t), yaw: frame.yaw, pitch: frame.pitch };
      this.world.step(new Map([[this.player.id, frame]]));
    }
    this.stats.ticks++;
    this.ghost?.tick(this.player);
    this.prev = this.cur;
    this.cur = snap(this.player);
    const drained = this.world.drainEvents();
    for (const ev of drained) this.onEvent(ev);
    this.campaign?.tick(drained);
    this.contestTick();
    this.streetTalk();
    this.footsteps();
    this.renderer.syncDummies(this.world.dummies);
  }

  private chargeTick = 0;
  /** True while the local file is standing inside the district's contest block. */
  private contestInside = false;
  /** The street has already said its one line this visit. */
  private streetHeard = false;

  /** One bark, once, when the file stands on the fixer's post. A quiet street says nothing. */
  private streetTalk(): void {
    if (!this.world.contestAt || this.streetHeard) return;
    const line = streetLine(this.world.level.name);
    if (!line) return;
    const spot = fixerOf(this.world.level);
    if (!spot || !inStreetTalk(this.player.pos.x, this.player.pos.z, spot)) return;
    this.streetHeard = true;
    this.cityLog.push(line);
    this.hud.alert(line, false, 4);
  }

  /** One line when the file steps into the block, and one when it steps out. The guns do not change. */
  private contestTick(): void {
    if (!this.world.contestAt) return;
    const inside = this.world.contestAt(this.player.pos.x, this.player.pos.z);
    if (inside === this.contestInside) return;
    this.contestInside = inside;
    this.renderer.campaignFx.setContestHot(inside);
    const stadium = streetCast(this.world.level.name)?.stadium ?? "THE BLOCK";
    this.hud.alert(inside ? `STADIUM ${stadium} · SHOOTING CONTEST · FALL AND THE SCRIP HITS THE GROUND` : "OUT OF THE BLOCK · THE POCKET STAYS", inside);
  }

  /** Latest wake state for the HUD (offline: the world's; online: the snapshot's). */
  /**
   * The round clock when the KERNEL last pulsed, or when this round's wake began (Stage 87). The
   * cadence is fixed, so one mark places every pulse after it — and nothing new goes on the wire.
   */
  private kernelMark: number | null = null;
  /** what each node's hold was doing last frame, for the readout's clock (Stage 85) */
  private nodeBook = new Map<number, TrackedNode>();
  private nodeBookTick = 0;
  private wakeHud: { phase: string; timeLeft: number; score: [number, number, number]; winner: number; nodes: { id: number; label: string; pos: { x: number; y: number; z: number }; owner: number; hold: number; contested: boolean; puller: number }[] } | null = null;
  private lastNodeOwners = new Map<number, number>();

  private nodeViewsOffline(): NodeView[] {
    const w = this.world.wake;
    if (!w) return [];
    return w.nodes.map((n) => ({ id: n.id, label: n.label, pos: n.pos, owner: n.owner, hold: n.hold, contested: n.contested, puller: n.puller, boost: n.boost > 0, links: n.links }));
  }

  /** The room's view of the run (online). */
  private onRunMsg(m: RunMsg): void {
    const prev = this.runView;
    this.runView = { carried: m.carried, banked: m.banked, banking: m.banking, inSafe: m.inSafe, zone: m.zone, claims: m.claims, zones: m.zones, today: m.today, cap: m.cap, owed: m.owed };
    this.runMoments(prev, this.runView);
    if (prev && !prev.inSafe && m.inSafe) this.hud.push(`SAFE ZONE · ${m.zone}`, "cy");
    this.applyRunView();
  }

  /**
   * The run's money moments (Stage 101), read from two views of it and heard the same way offline
   * and online: a claim taken in its own voice, a bank as the stamp it always was, and the drop —
   * a death in the PvP zone with something carried — as the fall it is, with a line for each.
   */
  private runMoments(prev: { carried: number; banked: number } | null, next: { carried: number; banked: number; zone: string | null }): void {
    for (const m of runMoments(prev, next)) {
      if (m.kind === "pickup") this.audio.claim(this.world.level.name);
      else if (m.kind === "bank") this.audio.sign(this.world.level.name);
      else this.audio.dropClaims(this.world.level.name);
      this.hud.push(momentLine(m, next.zone, this.file.depth), m.kind === "drop" ? "mg" : "am");
    }
  }

  private applyRunView(): void {
    const v = this.runView;
    this.renderer.run.syncZones(v?.zones ?? []);
    this.renderer.run.syncClaims(v?.claims ?? []);
    this.hud.setRun(v ? { carried: v.carried, banked: v.banked, banking: v.banking, inSafe: v.inSafe, zone: v.zone, today: v.today, cap: v.cap, owed: v.owed, claims: v.claims.length, depth: this.file.depth } : null);
  }

  /**
   * The round this weapon throws, if it throws one rather than firing a ray. Read from the same
   * weapon definition the sim launches from, so the mark and the round agree (Stage 78).
   */
  private arcOf(p: PlayerState): ArcSpec | null {
    const pr = weaponDefOf(p).projectile;
    return pr ? { speed: pr.speed, gravity: pr.gravity, fuse: pr.fuse, vel: { x: p.vel.x, y: p.vel.y, z: p.vel.z }, muzzle: 0.6 } : null;
  }

  /** the hits taken lately, for the HUD's bearings (Stage 74) */
  private hits: HitSource[] = [];

  /**
   * The file closed (Stage 83). Until now this was one line that named nobody — you died, the
   * camera went on looking wherever your hand had left it, and who did it was a question for the
   * kill feed. The camera turns onto whatever closed the file and the line says who it was.
   */
  private fileClosed(killerId: number): void {
    const who = this.closedBy(killerId);
    const d = who.at ? Math.hypot(who.at.x - this.player.pos.x, who.at.z - this.player.pos.z) : 0;
    this.hud.alert(who.name ? `◆ FILE CLOSED BY ${who.name}${who.at ? ` · ${d.toFixed(0)} M` : ""} — RE-LEASING IN 3S` : "◆ FILE CLOSED — RE-LEASING IN 3S", true, 3);
    this.renderer.post.kick(1);
    this.renderer.die(who.at);
    this.closedByLast.name = who.name;
    this.closedByLast.x = who.at?.x ?? 0;
    this.closedByLast.z = who.at?.z ?? 0;
    this.closedByLast.known = !!who.at;
  }

  /** HUD flag plus the two-tone, once a second, online and off (Stage 194). */
  private mechHasYou(): void {
    this.hud.flagged();
    if (this.world.tick % 30 === 0) this.audio.flagged(this.world.level.name);
  }

  /** Offline this is a sim event; online it is the snapshot's dead→alive edge (Stage 191). */
  private backOnTheLedger(): void {
    this.audio.respawn(this.world.level.name);
    this.hud.push(`◆ BACK ON THE LEDGER · ${districtName(this.world.level)}`, "cy");
  }

  /** what closed the file last, for the check that the camera found it */
  readonly closedByLast = { name: "", x: 0, z: 0, known: false };

  /** who closed it: a name to print, and a place to look, if either can be known from here */
  private closedBy(id: number): { name: string; at: { x: number; y: number; z: number } | null } {
    if (!id || id === this.player.id) return { name: "", at: null };
    if (this.net) {
      const r = this.net.remoteViews().find((x) => x.id === id);
      if (r) return { name: r.name || `FILE #${id}`, at: { x: r.x, y: r.y + r.height * 0.6, z: r.z } };
      // the wire folds the PvE cast into ids above two hundred, which names them without placing them
      return { name: id >= 200 ? "VANTAGE" : `FILE #${id}`, at: null };
    }
    const w = this.world;
    const p = w.players.get(id);
    if (p) return { name: p.name || `FILE #${id}`, at: { x: p.pos.x, y: p.pos.y + p.height * 0.6, z: p.pos.z } };
    const d = w.dummies.find((x) => x.id === id);
    if (d) return { name: `DUMMY-${String(d.id).padStart(2, "0")}`, at: { x: d.pos.x, y: d.pos.y + 1, z: d.pos.z } };
    const wasp = w.wasps.find((x) => x.id === -id || x.id === id);
    if (wasp) return { name: "WASP", at: { x: wasp.pos.x, y: wasp.pos.y, z: wasp.pos.z } };
    const m = w.mechs.find((x) => x.id === -id || x.id === id);
    if (m) return { name: "MECH", at: { x: m.pos.x, y: m.pos.y + 2, z: m.pos.z } };
    return { name: "", at: null };
  }

  /** where whatever hurt us was standing: a player, a dummy, a wasp, a mech */
  private attackerAt(by: number): { x: number; z: number } | null {
    if (by === this.player.id) return null;
    const w = this.world;
    const p = w.players.get(by);
    if (p) return { x: p.pos.x, z: p.pos.z };
    const d = w.dummies.find((x) => x.id === by);
    if (d) return { x: d.pos.x, z: d.pos.z };
    const wasp = w.wasps.find((x) => x.id === -by || x.id === by);
    if (wasp) return { x: wasp.pos.x, z: wasp.pos.z };
    const m = w.mechs.find((x) => x.id === -by || x.id === by);
    if (m) return { x: m.pos.x, z: m.pos.z };
    return null;
  }

  /**
   * A hit landed: remember where it came from, so the HUD can point at it, and shove the body away
   * from it. The bearing here is the sim's yaw of the direction back toward the attacker; the HUD's
   * own screen bearing is `damage.ts`'s, which is the negation.
   */
  /**
   * A round of mine landed on a body (Stage 91). The book is pruned on the way in rather than per
   * frame: it only ever grows when something is hit, so that is the only moment it can need it.
   */
  private landed(key: string, hit: LandedHit): void {
    rememberHit(this.closeBook, key, hit);
    forgetOldHits(this.closeBook, hit.at);
  }

  /**
   * A VANTAGE body's health and its maximum, from the sim offline or the wire online (a wasp's
   * comes whole, a mech's halved to fit the byte), or null when the body is not known.
   */
  private vantageHealth(kind: "wasp" | "mech", id: number): { health: number; max: number } | null {
    if (!this.net) {
      if (kind === "wasp") {
        const w = this.world.wasps.find((x) => x.id === id);
        return w ? { health: w.alive ? w.health : 0, max: WASP.health } : null;
      }
      const m = this.world.mechs.find((x) => x.id === id);
      return m ? { health: m.alive ? m.health : 0, max: MECH.health } : null;
    }
    const e = this.netEntities.find((x) => x.kind === (kind === "wasp" ? ENT_WASP : ENT_MECH) && x.id === id);
    if (!e) return null;
    return kind === "wasp" ? { health: e.a === 1 ? e.b : 0, max: WASP.health } : { health: e.a === 1 ? e.b * 2 : 0, max: MECH.health };
  }

  /**
   * Wasps and mechs the room is drawing. Empty offline, and empty online until the first snapshot.
   * A kill beam uses this list so it stands on the body you can see, not the level's spawn.
   */
  liveMarks(): { kind: "wasp" | "mech"; alive: boolean; x: number; y: number; z: number }[] {
    if (!this.net) return [];
    const out: { kind: "wasp" | "mech"; alive: boolean; x: number; y: number; z: number }[] = [];
    for (const e of this.netEntities) {
      if (e.kind === ENT_MECH) out.push({ kind: "mech", alive: e.a === 1, x: e.x, y: e.y, z: e.z });
      else if (e.kind === ENT_WASP) out.push({ kind: "wasp", alive: e.a === 1, x: e.x, y: e.y, z: e.z });
    }
    return out;
  }

  /** last frame's state by wasp, and the clock at each wasp's last cue (Stage 98) */
  private waspPrev = new Map<number, number>();
  private waspCued = new Map<number, number>();

  /**
   * The wasp that found you (Stage 98): a patrolling wasp turning to chase announced itself with a
   * point light and nothing else. Runs on the same per-frame list the renderer receives, offline
   * and online, and says only what the client knows — a wasp gone live near you, not whose.
   */
  private waspAlarm(wasps: readonly { id: number; pos: { x: number; y: number; z: number }; alive: boolean; state: number }[]): void {
    const seen: WaspSeen[] = wasps.filter((x) => x.alive).map((x) => ({ id: x.id, state: x.state, x: x.pos.x, y: x.pos.y, z: x.pos.z }));
    const at = this.listenPoint();
    // the cooldown runs on the simulation's clock, not the frame's (Stage 85): a probe that drives
    // the simulation by hand renders a few frames per section, and on the render clock a cue from
    // a whole section ago is still "five seconds ago"
    for (const cue of waspLocks(this.waspPrev, this.waspCued, seen, { x: at.x, y: this.player.pos.y + 1.6, z: at.z, yaw: at.yaw }, this.stats.ticks * SIM_DT)) {
      this.audio.waspLock(cue, this.world.level.name);
      this.hud.push(`◆ WASP LIVE · ${Math.round(cue.distance)} M ${cue.pan > 0.3 ? "RIGHT" : cue.pan < -0.3 ? "LEFT" : Math.abs(cue.bearing) > Math.PI / 2 ? "BEHIND" : "AHEAD"}`, "am");
    }
  }

  /**
   * Every live charge the client knows about, from whichever source is driving the world: the
   * simulation's own list offline, and the room's entity list online, where a projectile carries its
   * kind and where it is and nothing else (Stage 93).
   */
  private liveProjectiles(): LiveProjectile[] {
    if (!this.net) return this.world.projectiles.map((p) => ({ id: p.id, kind: p.kind, x: p.pos.x, y: p.pos.y, z: p.pos.z }));
    return this.netEntities.filter((e) => e.kind === ENT_PROJECTILE).map((e) => ({ id: e.id, kind: PROJ_KINDS[e.a] ?? "phage", x: e.x, y: e.y, z: e.z }));
  }

  private tookHit(x: number, z: number, damage: number): void {
    this.hits.push({ x, z, at: this.renderer.clockNow, damage });
    this.hits = pruneHits(this.hits, this.renderer.clockNow, wedgeLife(this.world.level.name));
    this.renderer.takeHit(Math.atan2(-(x - this.player.pos.x), -(z - this.player.pos.z)), Math.min(1, 0.35 + damage / 60));
  }

  /**
   * The bodies a shot from here could hit, in the terms the sim's hitscan tests them: the reticle
   * is cast against these as well as the level, so what it covers is what the shot hits rather than
   * the wall behind it (Stage 66). Same radii and heights as `castRay`, or the reticle would be
   * marking a volume that does not exist.
   */
  private aimTargets(): AimTarget[] {
    const out: AimTarget[] = [];
    const w = this.world;
    for (const d of w.dummies) if (d.alive) out.push({ pos: d.pos, radius: DUMMY_RADIUS, height: DUMMY_HEIGHT });
    if (this.net) {
      for (const r of this.net.remoteViews()) if (r.alive) out.push({ pos: { x: r.x, y: r.y, z: r.z }, radius: MOVE.capsuleRadius, height: r.height });
      for (const e of this.netEntities) {
        if (e.kind === ENT_WASP && e.a === 1) out.push({ pos: { x: e.x, y: e.y - WASP_HEIGHT / 2, z: e.z }, radius: WASP_RADIUS, height: WASP_HEIGHT });
        if (e.kind === ENT_MECH && e.a === 1) out.push({ pos: { x: e.x, y: e.y, z: e.z }, radius: MECH_RADIUS, height: MECH_HEIGHT });
      }
    } else {
      for (const o of w.players.values()) if (o.alive && o.id !== this.player.id) out.push({ pos: o.pos, radius: MOVE.capsuleRadius, height: o.height });
      for (const x of w.wasps) if (x.alive) out.push({ pos: { x: x.pos.x, y: x.pos.y - WASP_HEIGHT / 2, z: x.pos.z }, radius: WASP_RADIUS, height: WASP_HEIGHT });
      for (const m of w.mechs) if (m.alive) out.push({ pos: m.pos, radius: MECH_RADIUS, height: MECH_HEIGHT });
    }
    return out;
  }

  private syncOfflineEntities(): void {
    const w = this.world;
    if (w.run) {
      const v = runView(w.run, this.player.id, this.player.pos);
      const prev = this.runView;
      this.runView = { ...v, today: 0, cap: 0, owed: 0 };
      this.runMoments(prev, this.runView);
      this.applyRunView();
    }
    if (w.wake) {
      const views = this.nodeViewsOffline();
      this.renderer.wake.sync(views);
      this.wakeHud = { phase: w.wake.phase, timeLeft: w.wake.timeLeft, score: w.wake.score, winner: w.wake.winner, nodes: views };
    }
    this.renderer.fx.syncProjectiles(w.projectiles.map((p) => ({ id: p.id, kind: p.kind, pos: p.pos, stuck: p.stuck })));
    this.renderer.fx.syncClouds(w.clouds.map((c) => ({ id: c.id, pos: c.pos, radius: c.radius })));
    const wasps = w.wasps.map((x) => ({ id: x.id, pos: x.pos, yaw: x.yaw, alive: x.alive, state: x.disabledTimer > 0 ? 2 : x.state === "chase" ? 1 : 0 }));
    this.renderer.fx.syncWasps(wasps);
    this.waspAlarm(wasps);
    this.renderer.fx.syncMechs(w.mechs.map((m) => ({ id: m.id, pos: m.pos, yaw: m.yaw, lightYaw: m.face + m.lightYaw, alive: m.alive, locked: m.targetId >= 0 })));
  }

  private netEntities: NetSnapshot["entities"] = [];

  private netMatch: NetSnapshot["match"] = null;

  private syncNetEntities(): void {
    const ents = this.netEntities;
    const labels = ["", "A", "B", "C", "D", "E", "F", "G", "H"];
    const nodes: NodeView[] = ents
      .filter((e) => e.kind === ENT_NODE)
      .map((e) => ({ id: e.id, label: labels[e.id] ?? String(e.id), pos: { x: e.x, y: e.y, z: e.z }, owner: e.a, hold: e.b / 100, contested: !!(e.c & 1), puller: (e.c >> 1) & 3, boost: !!(e.c & 8), links: [1, 2, 3, 4, 5, 6, 7, 8].filter((l) => e.d & (1 << l)) }));
    if (nodes.length) {
      this.renderer.wake.sync(nodes);
      const m = this.netMatch;
      this.wakeHud = { phase: m ? (m.phase === 0 ? "warmup" : m.phase === 1 ? "wake" : "results") : "wake", timeLeft: m?.timeLeft ?? 0, score: [0, m?.score1 ?? 0, m?.score2 ?? 0], winner: m?.winner ?? 0, nodes };
    }
    this.renderer.fx.syncProjectiles(ents.filter((e) => e.kind === ENT_PROJECTILE).map((e) => ({ id: e.id, kind: PROJ_KINDS[e.a] ?? "phage", pos: { x: e.x, y: e.y, z: e.z }, stuck: e.b === 1 })));
    this.renderer.fx.syncClouds(ents.filter((e) => e.kind === ENT_CLOUD).map((e) => ({ id: e.id, pos: { x: e.x, y: e.y, z: e.z }, radius: e.c / 100 })));
    const wasps = ents.filter((e) => e.kind === ENT_WASP).map((e) => ({ id: e.id, pos: { x: e.x, y: e.y, z: e.z }, yaw: e.c / 1000, alive: e.a === 1, state: e.d }));
    this.renderer.fx.syncWasps(wasps);
    this.waspAlarm(wasps);
    this.renderer.fx.syncMechs(ents.filter((e) => e.kind === ENT_MECH).map((e) => ({ id: e.id, pos: { x: e.x, y: e.y, z: e.z }, yaw: e.c / 1000, lightYaw: e.d / 1000, alive: e.a === 1, locked: false })));
  }

  private footsteps(): void {
    if (this.player.weapon.charging) {
      this.chargeTick++;
      if (this.chargeTick % 6 === 0) this.audio.charge(this.player.weapon.charge, this.world.level.name);
    }
    const p = this.player;
    const sp = lenXZ(p.vel);
    if (p.grounded && sp > 0.8 && p.stance !== "slide") {
      this.stepDist += sp * SIM_DT;
      const stride = strideOf(sp, p.stance, this.world.level.name);
      if (this.stepDist >= stride) {
        this.stepDist = 0;
        this.stepSide = -this.stepSide;
        this.audio.footstep(sp, this.stepSide * 0.25, this.world.level.name);
      }
    } else this.stepDist = Math.min(this.stepDist, 0.5);
    this.otherSteps();
  }

  /**
   * And everyone else's (Stage 80). The file has heard its own boots since the first stage and
   * nobody else's: another player could cross the street behind it at seven metres a second in
   * silence. The rule is `client/steps.ts`; this hands it the bodies the wire (or the offline
   * world) already carries and plays what it returns.
   */
  private otherSteps(): void {
    const w: Walker[] = [];
    if (this.net) {
      for (const r of this.net.remoteViews()) w.push({ id: r.id, x: r.x, z: r.z, speed: Math.hypot(r.vx, r.vz), stance: r.stance, grounded: r.grounded, alive: r.alive });
    } else {
      for (const o of this.world.players.values()) {
        if (o.id === this.player.id) continue;
        w.push({ id: o.id, x: o.pos.x, z: o.pos.z, speed: lenXZ(o.vel), stance: o.stance, grounded: o.grounded, alive: o.alive });
      }
      // the dummies are drones on a rail and carry no velocity to read; the cast that walks is the
      // players, whether a person is driving them or VANTAGE is
    }
    if (w.length === 0) return;
    const p = this.player;
    for (const cue of stepCues(w, this.listenPoint(), this.stepBook, SIM_DT, this.world.level.name)) {
      this.audio.otherStep(cue.speed, cue.pan, cue.gain, this.world.level.name);
      this.heard.n++;
      this.heard.pan = cue.pan;
      this.heard.gain = cue.gain;
      this.heard.distance = cue.distance;
    }
  }
  /** metres each other body has walked since its last step */
  private stepBook = new Map<number, number>();
  /** the last step this file heard from somebody else: what the check reads, and nothing else */
  readonly heard = { n: 0, pan: 0, gain: 0, distance: 0 };
  /** and the last shot, the same way (Stage 81) */
  readonly heardShot = { n: 0, pan: 0, gain: 0, delay: 0, distance: 0 };
  /** the last round heard going past (Stage 99), for the probe: how many, which side, how close */
  readonly heardSnap = { n: 0, pan: 0, distance: 0 };
  private ammoWatch = { slot: -1, ammo: 0 };
  /** the shots the map has heard (Stage 104) */
  private pings: Ping[] = [];
  private shieldWatch: { shield: number; maxShield: number; alive: boolean } | null = null;

  /**
   * A shot somebody else fired, from `from` to `to`, that did not land on this file: if it came
   * within reach of the head it is heard going past, at the ear it passed (Stage 99). A round that
   * landed is heard as the hit it was, not as a miss.
   */
  private passedBy(from: { x: number; y: number; z: number }, to: { x: number; y: number; z: number }, landedOnMe: boolean): void {
    if (landedOnMe) return;
    const at = this.listenPoint();
    const cue = shotPass(from, to, { x: at.x, y: this.player.pos.y + 1.6, z: at.z, yaw: at.yaw });
    if (!cue) return;
    this.audio.snap(cue, this.world.level.name);
    this.heardSnap.n++;
    this.heardSnap.pan = cue.pan;
    this.heardSnap.distance = cue.distance;
  }

  /**
   * Where the file is listening from, and which way it is facing while it does. The look is the
   * live mouse angle when the pointer is locked, for the same reason the reticle is (Stage 73):
   * everything else on screen has already turned by the time the tick lands.
   */
  private listenPoint(): { x: number; z: number; yaw: number } {
    const p = this.player;
    return { x: p.pos.x, z: p.pos.z, yaw: this.input.isLocked && !this.bot ? this.input.yaw : p.yaw };
  }

  private onEvent(ev: SimEvent): void {
    this.recentEvents.push(ev);
    if (this.recentEvents.length > 1500) this.recentEvents.shift();
    switch (ev.type) {
      case "shot": {
        const mine = ev.playerId === this.player.id;
        const def = ev.weapon === "wasp" ? null : WEAPONS[ev.weapon];
        const color = def?.tracer ?? 0xffb02e;
        if (mine) this.audio.shot(ev.weapon, this.altFire.tick === ev.tick && this.altFire.alt, this.world.level.name);
        else {
          // somebody else's gun (Stage 81): until now a file could empty a magazine at you from
          // across the street in silence, and the first you knew of it was the integrity bar. The
          // muzzle is where the shot came from, so this is the flash's own position, not the
          // shooter's body a tick later
          const cue = gunCue(ev.from.x, ev.from.z, this.listenPoint(), this.world.level.name);
          if (cue) {
            this.audio.otherShot(ev.weapon, cue, this.world.level.name);
            this.heardShot.n++;
            this.heardShot.pan = cue.pan;
            this.heardShot.gain = cue.gain;
            this.heardShot.delay = cue.delay;
            this.heardShot.distance = cue.distance;
            // and the map marks where it went off (Stage 104)
            this.pings = rememberPing(this.pings, { x: ev.from.x, z: ev.from.z, at: this.hud.mapClock, kind: ev.weapon === "wasp" ? "wasp" : "file" });
          }
          // and if it went past the head, the round itself is heard where it was nearest (Stage 99)
          this.passedBy(ev.from, ev.to, ev.hits.some((h) => h.kind === "player" && h.id === this.player.id));
        }
        if (ev.weapon === "longwave") this.renderer.fx.beam(ev.from, ev.to, color, 0.05, 0.6);
        else this.renderer.tracer(ev.from, ev.to, ev.hit.kind === "world", !mine, color);
        if (!this.net && ev.hits.length) {
          // offline the event carries the exact damage the simulation applied, so nothing has to be
          // re-derived. The impact point is the ray's end, which is the first body it found unless
          // the round pierced — and then a spark on the front one is the honest read anyway.
          const fromYaw = lookYawPitch(ev.to, ev.from).yaw;
          for (const h of ev.hits) if (h.kind !== "world" && h.kind !== "none") this.renderer.hitBody(h.kind, h.id, ev.to, impactRead(h.damage), fromYaw, color);
          if (mine) {
            const h = ev.hits[0]!;
            this.audio.hit(h.zone ?? "body", this.world.level.name);
            if (this.hitmarkers) this.hud.flashHit();
            for (const x of ev.hits) if (x.kind !== "world" && x.kind !== "none") this.landed(bodyKey(x.kind, x.id), { at: this.renderer.clockNow, zone: x.zone ?? "body", distance: Math.hypot(ev.to.x - ev.from.x, ev.to.y - ev.from.y, ev.to.z - ev.from.z), weapon: ev.weapon });
          }
        }
        break;
      }
      case "melee":
        if (ev.playerId === this.player.id) {
          this.audio.shot(ev.weapon, ev.lunge, this.world.level.name);
          const landed = ev.hits.find((h) => h.kind === "dummy" || h.kind === "player" || h.kind === "wasp" || h.kind === "mech");
          if (landed && ev.at && (landed.kind === "dummy" || landed.kind === "player" || landed.kind === "wasp" || landed.kind === "mech")) {
            this.audio.hit("body", this.world.level.name);
            this.renderer.hitBody(landed.kind, landed.id, ev.at, impactRead(landed.damage), this.player.yaw, WEAPONS[ev.weapon].tracer);
          }
          if (ev.lunge) this.renderer.post.kick(0.4);
        }
        break;
      case "explode":
        this.renderer.fx.explosion(ev.pos, ev.radius, ev.projKind === "frag" ? 0xffb02e : 0x8f4dff, ev.projKind === "frag");
        this.audio.explosion(ev.projKind === "frag", this.world.level.name);
        if (Math.hypot(ev.pos.x - this.player.pos.x, ev.pos.z - this.player.pos.z) < ev.radius * 2) this.renderer.post.kick(0.6);
        break;
      case "cloud":
        this.audio.smoke(this.world.level.name);
        break;
      case "emp":
        this.audio.emp(this.world.level.name);
        this.renderer.fx.explosion(ev.pos, ev.radius, 0x35f2ff, false);
        if (Math.hypot(ev.pos.x - this.player.pos.x, ev.pos.z - this.player.pos.z) < ev.radius) this.renderer.post.kick(1);
        break;
      case "stun":
        if (ev.playerId === this.player.id) {
          this.audio.stun(this.world.level.name);
          this.renderer.post.kick(0.8);
        }
        break;
      case "flagged":
        if (ev.playerId === this.player.id) this.mechHasYou();
        break;
      case "mechBeam":
        this.renderer.fx.beam(ev.from, ev.to, 0xffb02e, 0.08, 0.35);
        if (ev.playerId === this.player.id) {
          this.audio.mechBeam(this.world.level.name);
          this.renderer.post.kick(0.5);
        }
        break;
      case "hurt":
        if (ev.playerId === this.player.id) {
          this.audio.hurt(this.world.level.name);
          this.hud.alert(`▲ INTEGRITY −${ev.damage}`, true, 0.8);
          const from = this.attackerAt(ev.by);
          if (from) this.tookHit(from.x, from.z, ev.damage);
        }
        break;
      case "swap":
        if (ev.playerId === this.player.id) this.audio.swap(this.world.level.name);
        break;
      case "reloadSeat":
        if (ev.playerId === this.player.id) this.audio.reload("seat", this.world.level.name);
        break;
      case "reloadCancel":
        break;
      case "chargeStart":
      case "chargeCancel":
        break;
      case "altToggle":
        // the choke racking on or off (Stage 94): the simulation has said so since Stage 4 and the
        // client dropped it on the floor
        if (ev.playerId === this.player.id) this.audio.altToggle(ev.on, this.world.level.name);
        break;
      case "fire":
        // the trigger pull that names the round: it arrives in the same tick as the shot events it
        // produced, ahead of them, so the shot handler reads it back by tick rather than by guessing
        // from the weapon's state a frame later
        if (ev.playerId === this.player.id) this.altFire = { tick: ev.tick, alt: ev.alt };
        break;
      case "chargeFull":
        if (ev.playerId === this.player.id) this.audio.charge(1, this.world.level.name);
        break;
      case "lunge":
        if (ev.playerId === this.player.id) this.audio.jump(this.world.level.name);
        break;
      case "throw":
        if (ev.playerId === this.player.id) this.audio.throw(this.world.level.name);
        break;
      case "nodeFlip": {
        const n = this.world.wake?.nodes.find((x) => x.id === ev.node);
        if (n) this.renderer.wake.flip(n.pos, ev.team);
        this.audio.nodeFlip(ev.team === this.player.team, this.world.level.name);
        this.renderer.post.kick(0.5);
        this.hud.push(`NODE ${n?.label ?? ev.node} ${ev.from === 0 ? "PULLED OFF THE MODEL" : "TAKEN"} — CELL ${ev.team === 1 ? "ONE" : "TWO"}`, ev.team === 1 ? "gr" : "cy");
        break;
      }
      case "nodeContest": {
        const n = this.world.wake?.nodes.find((x) => x.id === ev.node);
        this.hud.alert(`◆ NODE ${n?.label ?? ev.node} CONTESTED`, true, 1.5);
        this.audio.contest(this.world.level.name);
        break;
      }
      case "kernelPulse": {
        this.kernelMark = this.world.wake?.timeLeft ?? null;
        const n = this.world.wake?.nodes.find((x) => x.id === ev.node);
        this.hud.alert(`◆ KERNEL PULSE — NODE ${n?.label ?? ev.node} ${ev.released ? "RE-LEASED" : "DRAINED"}`, true, 2.5);
        this.audio.kernelPulse(this.world.level.name);
        this.renderer.post.kick(0.8);
        break;
      }
      case "phase":
        // the cadence runs from the round's start, so that is a mark too (Stage 87)
        this.kernelMark = ev.phase === "wake" ? (this.world.wake?.timeLeft ?? null) : null;
        this.hud.alert(ev.phase === "wake" ? wakeBeginsLine() : ev.phase === "results" ? roundOverLine(ev.winner, districtName(this.world.level)) : "◆ WARM-UP", false, 4);
        // the round's start and its end have their own voices (Stage 124); the KERNEL's pulse is
        // the warm-up's, the model taking the district back
        if (ev.phase === "wake") this.audio.wakeBegins(this.world.level.name);
        else if (ev.phase === "results") this.audio.roundOver(ev.winner === 0 ? "none" : ev.winner === this.player.team ? "won" : "lost", this.world.level.name);
        else this.audio.kernelPulse(this.world.level.name);
        this.renderer.post.kick(1);
        break;
      case "fullWake":
        this.hud.alert(fullWakeLine(districtName(this.world.level)), false, 5);
        this.audio.nodeFlip(true, this.world.level.name);
        this.renderer.post.kick(1);
        break;
      case "waspDeath":
        this.hud.push(`WASP-${String(ev.waspId).padStart(2, "0")} DOWNED`, "am");
        this.audio.explosion(false, this.world.level.name);
        break;
      case "mechDeath":
        this.hud.push(`REPO MECH ${ev.mechId} DISABLED — VANTAGE RE-LEASING`, "am");
        this.audio.explosion(true, this.world.level.name);
        this.renderer.post.kick(1);
        break;
      case "kill":
        this.audio.kill(this.killTier(ev.weapon), this.world.level.name);
        this.hud.killStamp(ev.victimKind, closeLine(closeRead(this.closeBook, bodyKey(ev.victimKind, ev.victimId), this.renderer.clockNow)));
        this.renderer.post.kick(1);
        this.hud.push(`BLANK ⟶ ${victimLabel(ev.victimKind)}-${String(ev.victimId).padStart(2, "0")} · ${weaponName(ev.weapon)}${ev.ttkTicks ? ttkNote(ev.ttkSeconds) : ""}`, "mg");
        break;
      case "slide":
        this.audio.slide(this.world.level.name);
        break;
      case "slideJump":
        this.audio.jump(this.world.level.name);
        break;
      case "jump":
        this.audio.jump(this.world.level.name);
        break;
      case "land":
        this.audio.land(ev.speed, this.world.level.name);
        break;
      case "mantle":
        // the cue says it; the log is for what happened on the ledger, not every ledge climbed or
        // slide-jump landed (Stage 122: MANTLE and SLIDE-JUMP lines had pushed the kills off it)
        this.audio.mantle(this.world.level.name);
        break;
      case "reloadStart":
        this.audio.reload("start", this.world.level.name);
        break;
      case "reloadEnd":
        this.audio.reload("end", this.world.level.name);
        break;
      case "dryFire":
        this.audio.dryFire(this.world.level.name);
        break;
      case "dummyRespawn":
        this.hud.push(`DUMMY-${String(ev.dummyId).padStart(2, "0")} RE-LEASED`, "am");
        this.hud.alert(`◆ VANTAGE RE-LEASE — DUMMY-${String(ev.dummyId).padStart(2, "0")} BACK ON THE LEDGER`, true);
        break;
      case "death":
        if (ev.playerId === this.player.id) this.fileClosed(ev.killerId);
        break;
      case "mantleEnd":
      case "slideEnd":
        break;
      case "respawn":
        if (ev.playerId === this.player.id) this.backOnTheLedger();
        break;
      default:
        break;
    }
  }

  private frame(now: number, render: boolean): void {
    this.lastFrameAt = now;
    this.perf?.sample(now, render);
    if (this.last < 0) this.last = now;
    let dt = (now - this.last) / 1000;
    this.last = now;
    if (dt > 0.5) {
      // a tab switch or a very long hitch: drop the excess rather than simulate it
      this.stats.droppedTime += dt - 0.5;
      dt = 0.5;
    }
    if (this.realtime) {
      this.acc += dt;
      this.stats.realtimeWall += dt;
      if (dt > this.stats.maxFrameDt) this.stats.maxFrameDt = dt;
      let n = 0;
      while (this.acc >= SIM_DT && n < MAX_CATCHUP_TICKS) {
        this.tick();
        this.acc -= SIM_DT;
        n++;
      }
      this.stats.realtimeTicks += n;
      if (n === MAX_CATCHUP_TICKS && this.acc > SIM_DT) {
        this.stats.catchupHits++;
        this.stats.droppedTime += this.acc;
        this.acc = 0;
      }
    }
    // the band's two numbers are folded here, on every frame the loop is given (Stage 156): the
    // ticks come from every frame, so the clock they are divided by has to as well. Below the
    // drawn-frame return it measured only the time the renderer drew for, and said 120 Hz for a
    // sim running at 60
    {
      const step = perfStep(this.fpsWindow, dt, render && this.drawing, this.stats.ticks);
      this.fpsWindow = step.window;
      if (step.read) {
        this.stats.fps = step.read.fps;
        this.stats.simHz = step.read.simHz;
      }
    }
    const alpha = this.realtime ? Math.min(1, this.acc / SIM_DT) : 1;
    const a = this.prev;
    const b = this.cur;
    const p = this.player;
    const view: ViewState = {
      x: a.x + (b.x - a.x) * alpha,
      y: a.y + (b.y - a.y) * alpha,
      z: a.z + (b.z - a.z) * alpha,
      eye: b.eye,
      yaw: lerpAngle(a.yaw, b.yaw, alpha),
      pitch: a.pitch + (b.pitch - a.pitch) * alpha,
      kickPitch: a.kickPitch + (b.kickPitch - a.kickPitch) * alpha,
      kickYaw: a.kickYaw + (b.kickYaw - a.kickYaw) * alpha,
      speed: lenXZ(p.vel),
      vy: p.vel.y,
      landVy: p.landVy,
      grounded: p.grounded,
      stance: p.stance,
      reloading: p.weapon.reloadTimer > 0 && p.weapon.reloadTotal > 0 ? 1 - p.weapon.reloadTimer / p.weapon.reloadTotal : 0,
      slot: p.weapon.slot,
      zoom: p.weapon.altActive && weaponDefOf(p).alt.kind === "ads" ? weaponDefOf(p).alt.zoom ?? 1 : 1,
      charge: p.weapon.charging ? p.weapon.charge : 0,
      stunned: p.weapon.stunTimer > 0,
      alive: p.alive,
      moveYaw: lenXZ(p.vel) > 0.5 ? Math.atan2(-p.vel.x, -p.vel.z) : p.yaw,
      height: p.height,
      // What a shot would do from here, against the bodies the sim's hitscan tests (Stage 66) — but
      // along the aim the camera is drawn with, which is the aim plus the *view* share of the
      // recoil. Recoil is split sixty/forty (RECOIL_VIEW_SHARE): sixty per cent moves the view, and
      // the forty the sim adds to the shot on top of that is the pattern, which a player is meant
      // to learn from where the tracers go. Adding it here marked the true shot in third person and
      // nowhere else, so the same weapon climbed predictably in one view and was solved in the
      // other — one game with two skill floors (Stage 76).
      aimYaw: p.yaw + p.weapon.kickYaw,
      aimPitch: p.pitch + p.weapon.kickPitch,
      // how far a shot from this weapon reaches: the reticle stops where the shot does, so it
      // cannot mark a body at sixty metres for a weapon whose rounds die at thirty (Stage 73)
      aimRange: weaponDefOf(p).range.max,
      // and a round that falls is not a ray at all: for a launcher the reticle walks the arc the
      // sim integrates, from the same launch — the weapon's speed and gravity, plus the half of the
      // file's own motion the round inherits (Stage 78)
      arc: this.arcOf(p),
      targets: this.aimTargets(),
    };
    this.input.currentSlot = p.weapon.slot;
    if (this.touch) this.touch.currentSlot = p.weapon.slot;
    // Local view is not interpolated when the keyboard drives it: mouse look
    // must feel immediate, so use the live input angles.
    if (!this.bot && this.input.isLocked) {
      view.yaw = this.input.yaw;
      view.pitch = this.input.pitch;
      // and the shot's direction with it. The camera is drawn along the live mouse angles because
      // mouse look must feel immediate; leaving the reticle on the sim's last tick left it trailing
      // the camera through every flick, on exactly the path no probe drives — a bot never locks the
      // pointer, so this is the one path the checks could not see (Stage 73).
      view.aimYaw = this.input.yaw + p.weapon.kickYaw;
      view.aimPitch = this.input.pitch + p.weapon.kickPitch;
      if (lenXZ(p.vel) <= 0.5) view.moveYaw = this.input.yaw;
    }
    // how many files are in the room and what the link costs (Stages 149, 154): text, not a drawn
    // frame, so a client that is not drawing still says it
    this.hud.setRoom(this.net?.status === "joined", this.net?.files ?? 0, this.net?.rttMs ?? 0);
    if (!render || !this.drawing) return;
    if (this.net) this.renderer.syncRemotes(this.net.remoteViews().map((r) => ({ ...r, debt: r.id === this.debtTargetId })));
    if (!this.net) this.syncOfflineEntities();
    else this.syncNetEntities();
    const rdt = this.lastRenderAt < 0 ? dt : Math.min(0.5, (now - this.lastRenderAt) / 1000);
    this.lastRenderAt = now;
    if (this.ghost && this.renderer.hub) {
      this.ghostPose = this.ghost.pose();
      this.renderer.hub.setGhost(this.ghostPose);
    }
    this.syncTram();
    this.renderer.render(view, rdt);
    const shown = this.renderer.view();
    this.hud.setReticle({ ...shown.reticle, arc: shown.aim.arc });
    // and the cone the next round leaves in, at the reticle (Stage 108): the sim's own spread rule
    // for this weapon and alt, projected through this frame's camera
    this.hud.setSpread(coneRadiusPx(coneNow(weaponDefOf(p), p.weapon.altActive, modsFor(p).spread), shown.fov, this.hud.viewHeight));
    // and what my last round did to a VANTAGE body (Stage 103), read from the health the sim or
    // the wire already carries, for two seconds after the round landed
    {
      const fresh = freshestVantage(this.closeBook, this.renderer.clockNow);
      const hp = fresh ? this.vantageHealth(fresh.kind, fresh.id) : null;
      this.hud.setTarget(fresh && hp ? targetRead(fresh.kind, fresh.id, hp.health, hp.max, fresh.at, this.renderer.clockNow) : null);
    }
    // and where the last hits came from, relative to where the camera is looking now
    this.hud.setDamage(hitMarks(this.hits, p.pos.x, p.pos.z, view.yaw, this.renderer.clockNow, wedgeLife(this.world.level.name)));
    // a live charge near the file (Stage 93): until now a frag landing behind you was drawn in the
    // world and nowhere else, and four and a half metres of blast arrived out of silence
    this.hud.setThreats(threatMarks(this.liveProjectiles(), { x: p.pos.x, y: p.pos.y, z: p.pos.z, yaw: view.yaw }));
    if (this.renderer.life.tram?.passing) this.audio.tram(this.world.level.name);
    this.stats.frames++;
    // the shots the map heard (Stage 104), pruned on the map's own clock
    this.pings = prunePings(this.pings, this.hud.mapClock);
    this.hud.setRadarPings(this.pings);
    const held = this.file.mastery[weaponDefOf(p).id];
    const heldRank = this.file.mastery[weaponDefOf(p).id]?.rank ?? 1;
    const ch = held ? gateFor(weaponDefOf(p).id, held) : null;
    const gate = ch?.text ?? "";
    const have = ch && held ? (held.counters[ch.counter] ?? 0) : 0;
    const need = ch?.need ?? 0;
    const heldXp = this.file.mastery[weaponDefOf(p).id]?.xp ?? 0;
    const rackRanks = WEAPON_LIST.map((w) => this.file.mastery[w.id]?.rank ?? 1);
    this.hud.update(p, view.speed, this.stats.fps, this.realtime ? this.stats.simHz : SIM_HZ, this.world.dummies, rdt, heldRank, gate, have, need, heldXp, rackRanks);
    // Stage 143: the phone's grenade pads say what they throw and what the next tap selects
    this.touch?.setGrenades(p.weapon.grenadeSel, p.weapon.grenades, GRENADE_LIST.map((g) => g.name));
    this.touch?.setAltLabel(weaponDefOf(p).alt.kind === "lunge" ? "LUNGE" : "ALT");
    {
      const mag = ammoRead(p.weapon.ammo[p.weapon.slot] ?? 0, weaponDefOf(p).magSize, p.weapon.reloadTimer, p.weapon.reloadTotal, p.weapon.reloadSeated);
      this.touch?.setMagazineEmpty(reloadPadHot(mag.state));
      this.touch?.setMagazineLow(reloadPadLow(mag.state));
      this.touch?.setFireEmpty(firePadHot(mag.state));
      this.touch?.setFireLow(firePadLow(mag.state));
      this.touch?.setWeaponEmpty(weaponPadHot(mag.state));
      this.touch?.setWeaponLow(weaponPadLow(mag.state));
      this.touch?.setAltEmpty(altPadHot(mag.state));
      this.touch?.setAltLow(altPadLow(mag.state));
      this.touch?.setJumpEmpty(jumpPadHot(mag.state));
      this.touch?.setJumpLow(jumpPadLow(mag.state));
      this.touch?.setSlideEmpty(slidePadHot(mag.state));
      this.touch?.setSlideLow(slidePadLow(mag.state));
      this.touch?.setGrenadeEmpty(grenadePadHot(mag.state));
      this.touch?.setGrenadeLow(grenadePadLow(mag.state));
      this.touch?.setNextGrenadeEmpty(nextGrenadePadHot(mag.state));
      this.touch?.setNextGrenadeLow(nextGrenadePadLow(mag.state));
      this.touch?.setPauseEmpty(pausePadHot(mag.state));
      this.touch?.setPauseLow(pausePadLow(mag.state));
      this.touch?.setStickRingEmpty(stickRingHot(mag.state));
      this.touch?.setStickRingLow(stickRingLow(mag.state));
      this.touch?.setStickKnobEmpty(stickKnobHot(mag.state));
      this.touch?.setStickKnobLow(stickKnobLow(mag.state));
    }
    // the last quarter of the magazine is heard, once, on the round that crosses into it (Stage
    // 100); a swap starts the count over on the new weapon rather than comparing across guns
    {
      const slot = p.weapon.slot;
      const ammoNow = p.weapon.ammo[slot] ?? 0;
      if (slot === this.ammoWatch.slot && lastRoundsEdge(this.ammoWatch.ammo, ammoNow, weaponDefOf(p).magSize)) this.audio.lowAmmo(this.world.level.name);
      this.ammoWatch = { slot, ammo: ammoNow };
    }
    // the shield breaking and coming back (Stage 102): a cue and a line for each, and the bar says
    // which state it is in; read frame to frame like the run's money and the magazine's last quarter
    {
      const now = { shield: p.shield, maxShield: p.maxShield, alive: p.alive };
      for (const m of shieldMoments(this.shieldWatch, now)) {
        if (m === "broke") this.audio.shieldBreak(this.world.level.name);
        else this.audio.shieldBack(this.world.level.name);
        this.hud.push(shieldLine(m), m === "broke" ? "mg" : "cy");
      }
      this.shieldWatch = now;
      this.hud.setShieldBroken(p.alive && p.maxShield > 0 && p.shield <= 0);
    }
    // low health: the pulse until the shield is back (a real cue, not a HUD colour)
    const low = p.alive && p.health > 0 && p.health < 30;
    if (low || this.lowHealthOn) this.audio.lowHealth(low, performance.now(), this.world.level.name);
    this.lowHealthOn = low;
    if (this.wakeHud) {
      this.hud.setRadarNodes(this.wakeHud.nodes);
      this.hud.wake({ ...this.wakeHud, kernelIn: this.wakeHud.phase === "wake" ? kernelIn(this.wakeHud.timeLeft, this.kernelMark, WAKE.kernelPulseSeconds) : null }, p.team, p.stats);
      // and the one under your feet (Stage 85): the strip says who holds all eight, this says what
      // is happening to the one you are standing on. The rate is measured from the hold the server
      // is publishing, so the seconds are the simulation's own.
      // against the simulation's clock, not the frame's: a hold moves per tick
      const simDt = (this.stats.ticks - this.nodeBookTick) * SIM_DT;
      this.nodeBookTick = this.stats.ticks;
      trackHolds(this.nodeBook, this.wakeHud.nodes, simDt);
      const near = this.wakeHud.phase === "wake" ? nearestNode(this.wakeHud.nodes, p.pos, WAKE.nodeRadius * 2) : null;
      this.hud.nodeFoot(near ? nodeReadout(near.node, this.nodeBook.get(near.node.id), near.distance, WAKE.nodeRadius) : null, p.team);
    }
  }
}
